import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Text, ToastQueue } from '@adobe/react-spectrum';
import Comment from '@spectrum-icons/workflow/Comment';
import Calendar from '@spectrum-icons/workflow/Calendar';
import Circle from '@spectrum-icons/workflow/Circle';
import styles from './GeneralInfo.module.scss';
import slideoutStyles from '../SlideOutPanel.module.scss'; // For badge dropdown styles
import '../../Fields/Calendar/calendar.module.scss'; // Import calendar styles

import EditableField from '../Field/EditableField';
import { mockData } from '../configs/mockData';
import { slideoutFieldConfig } from '../configs/slideoutFieldConfig';
import { mockDropdownOptions } from '../configs/mockDropdownOptions';
import { usesPcoStylePhasedDates } from '../configs/momentTypeConstants';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import RiskLevelLowIcon from '../../../assets/icons/Risk_Low.svg';
import RiskLevelModerateIcon from '../../../assets/icons/Risk_Moderate.svg';
import RiskLevelHighIcon from '../../../assets/icons/Risk_High.svg';
import RiskLevelPlanningIcon from '../../../assets/icons/risk-level-planning.svg';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import ChevronDownRiskIcon from '../../../assets/icons/chevron-down-risk.svg';
import CheckmarkIcon from '../../../assets/icons/checkmark-icon.svg';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { CalendarRange } from '../../Fields/Calendar';
import { parseDate } from '@internationalized/date';
import '../../Fields/Calendar/styles.scss'; // Import calendar styles
import { EventData, ParentMomentData } from '../../../services/momentsApi';
import Switch from '../Switch/Switch';
import { createPortal } from 'react-dom';
import {
  updateMoment,
  patchRtmPhase,
  updateMomentCard
} from '../../RsdForms/api';
import {
  INHERIT_MILESTONE_TOOLTIP_MESSAGE,
  RISK_LEVELS,
  STATUS_VALUES
} from '../../../../utils/constants';
import { isSpecialDateStatus } from '../../../../utils/timelineDisplayDates';
import {
  updateNonPhasedField,
  updateEventField,
  updateParentMomentField
} from '../utils/fieldUpdateUtils';
import SimpleDateField from './SimpleDateField';
import {
  updatePhasedEventField,
  updatePhasedParentMomentField
} from '../utils/phasedFieldUpdateUtils';
import { normalizeStatus } from '../../../../utils/helpers';
import { useRBAC } from '../context/RBACContext';
import { useMomentContext } from '../context/MomentContext';
import {
  getDateFieldsForMomentType,
  hasSecondaryDateStatus,
  shouldShowRoadmapToggle
} from '../configs/momentTypeDateFieldConfig';
import type { AlertIssue } from '../../AlertsAndNotification/store/types';
import { useActiveAlertIssues } from '../../AlertsAndNotification/hooks/useActiveAlertIssues';
import { matchesGeneralInfoDateAlertIssue } from '../../AlertsAndNotification/utils/declarativeHighlight';

interface GeneralInfoProps {
  data: {
    id?: string; // Phase ID for phased layouts
    momentType: string;
    productGroups: string;
    categories: string;
    releasePhase: string;
    reportingStatus: string;
    riskLevel?: string; // Risk level for auto-populating status
    dates: {
      targetStart?: string;
      targetEnd?: string;
      releaseDate?: string;
      targetMonth?: string;
      targetQuarter?: string;
      releaseMonth?: string;
      releaseQuarter?: string;
      targetDateStatus?: string; // TBD or N/A for target dates
      releaseDateStatus?: string; // TBD or N/A for release dates
      effectiveDateStatus?: string; // TBD or N/A for effective dates
      effectiveDate?: string;
      effectiveMonth?: string;
      effectiveQuarter?: string;
      customerNotificationDate?: string;
      customerNotificationMonth?: string;
      customerNotificationQuarter?: string;
      customerNotificationDateStatus?: string;
    };
    tier: string;
    event: {
      id: string;
      name: string;
    };
    parentMoment?:
      | {
          id: string;
          name: string;
        }
      | string
      | null;
    // Optionally add any other fields you expect
  };
  onGeneralInfoChange?: (updated: any) => void;
  onNotificationRefresh?: (patchResponse?: unknown) => void; // Callback to refresh notifications after successful PATCH
  isCollapsible?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  phaseName?: string;
  showContent?: boolean;
  // Props for events data (passed from parent to avoid duplicate API calls)
  eventsData?: EventData[];
  isLoadingEvents?: boolean;
  eventsError?: string | null;
  // Props for parent moments data (passed from parent to avoid duplicate API calls)
  parentMomentsData?: ParentMomentData[];
  isLoadingParentMoments?: boolean;
  parentMomentsError?: string | null;
  // New prop to determine if this is a phased layout
  isPhased?: boolean;
  // Issue ID for API calls
  issueId?: string;
  /** Root moment display name (card title) when phase context differs */
  rootMomentName?: string;
  // Root moment type for fallback when phase generalInfo is null
  rootMomentType?: string;
  // Callback for milestone refresh on tier change
  onMilestoneRefresh?: (patchResponse?: unknown) => void;
  // Callback for soft refresh after show-on-roadmap / secondary-date-status updates (includes Features)
  onSeamlessRefresh?: (patchResponse?: unknown) => void;
  // Callback to refresh global notifications after toggle changes
  onRefreshNotifications?: () => void;
}

// Define dropdown fields based on layout type
const getAllDropdownFields = () => [
  { id: 'reportingStatus', label: 'STATUS', optionsKey: 'statuses' },
  { id: 'tier', label: 'TIER', optionsKey: 'tiers' },
  { id: 'releasePhase', label: 'RELEASE PHASE', optionsKey: 'releasePhases' },
  { id: 'productGroups', label: 'PRODUCT GROUP', optionsKey: 'productGroups' },
  { id: 'categories', label: 'CATEGORY', optionsKey: 'categories' }
];

const getPhasedDropdownFields = (): Array<{
  id: string;
  label: string;
  optionsKey: string;
}> => [
  // For phased layout: Remove Status, Product Group, Category, Release Phase, and Tier
  // Keep only Event and Date fields in general info
];

const getNonPhasedDropdownFields = (): Array<{
  id: string;
  label: string;
  optionsKey: string;
}> => [
  // Remove product group, categories, release phase, status and tier from non-phased layout
  // Status and tier are now handled in the moment type header
];

// Get date fields based on moment type configuration
const getDateFieldsByMomentType = (
  momentType: string | undefined,
  isPhased: boolean
) => {
  if (!momentType || momentType === 'Unknown') {
    // Fallback to default fields if moment type is not available or is "Unknown"
    return isPhased ? getPhasedDateFields() : getNonPhasedDateFields();
  }

  const configuredFields = getDateFieldsForMomentType(momentType);

  // If no configured fields found, fall back to default fields
  if (!configuredFields || configuredFields.length === 0) {
    return isPhased ? getPhasedDateFields() : getNonPhasedDateFields();
  }

  // Map the configured fields to the format expected by the component
  // Note: The component uses 'marketingMoment' as id for targetStart field for historical reasons
  return configuredFields.map(field => {
    // Map targetStart to marketingMoment for component compatibility
    const fieldId =
      field.fieldName === 'targetStart' ? 'marketingMoment' : field.fieldName;

    return {
      id: fieldId,
      label: field.label,
      type: 'single'
    };
  });
};

// Legacy functions - kept for backward compatibility but not used when moment type is available
const getAllDateFields = () => [
  { id: 'marketingMoment', label: 'MARKETING MOMENT', type: 'single' },
  { id: 'releaseDate', label: 'RELEASE DATE', type: 'single' }
];

const getPhasedDateFields = () => [
  { id: 'marketingMoment', label: 'MARKETING MOMENT', type: 'single' },
  { id: 'releaseDate', label: 'RELEASE DATE', type: 'single' }
];

const getNonPhasedDateFields = () => [
  { id: 'marketingMoment', label: 'MARKETING MOMENT', type: 'single' }
  // Remove releaseDate from non-phased scenarios
];

// Get additional date fields that should be shown when they exist in the data
// Note: Effective date is now handled by momentTypeDateFieldConfig, so this function
// is kept for potential future additional date fields but currently returns empty array
const getAdditionalDateFields = (
  data: any,
  isPhased: boolean = false
): Array<{
  id: string;
  label: string;
  type: string;
}> => {
  const additionalFields: Array<{
    id: string;
    label: string;
    type: string;
  }> = [];

  // No additional fields needed - all date fields are now handled by momentTypeDateFieldConfig
  // This function is kept for backward compatibility and potential future use

  return additionalFields;
};

const getAllSpecialFields = () => [
  { id: 'event', label: 'EVENT', optionsKey: 'events' },
  { id: 'parentMoment', label: 'PARENT MOMENT', optionsKey: 'parentMoments' }
];

const getPhasedSpecialFields = (momentType?: string) =>
  usesPcoStylePhasedDates(momentType)
    ? []
    : [
        { id: 'event', label: 'EVENT', optionsKey: 'events' },
        {
          id: 'parentMoment',
          label: 'PARENT MOMENT',
          optionsKey: 'parentMoments'
        }
      ];

const getNonPhasedSpecialFields = () => [
  { id: 'event', label: 'EVENT', optionsKey: 'events' }
  // Remove parentMoment from non-phased scenarios
];

const getOptionNameById = (
  options: any[],
  id: string | { id: string; name: string }
) => {
  if (!id) return '';
  if (typeof id === 'object' && id.name) return id.name;
  const found = options.find(opt => opt.id === id || opt.name === id);
  return found ? found.name : id;
};

const getOptionIdByName = (options: any[], name: string) => {
  const found = options.find(opt => opt.name === name);
  return found ? found.id : name;
};

const getEditableFieldType = (
  id: string
): 'status' | 'event' | 'parentMoment' | 'tier' | 'default' => {
  if (id === 'status' || id === 'reportingStatus') return 'status';
  if (id === 'event') return 'event';
  if (id === 'parentMoment') return 'parentMoment';
  if (id === 'tier') return 'tier';
  return 'default';
};

const getStatusIcon = (option: string) => {
  if (option === 'Risk Level: Low')
    return (
      <LegendRiskLowIcon style={{ width: 18, height: 18, marginRight: 4 }} />
    );
  if (option === 'Risk Level: Moderate')
    return (
      <LegendRiskModerateIcon
        style={{ width: 18, height: 18, marginRight: 4 }}
      />
    );
  if (option === 'Risk Level: High')
    return (
      <LegendRiskHighIcon style={{ width: 18, height: 18, marginRight: 4 }} />
    );
  if (option === 'Risk Level: In Planning')
    return (
      <LegendInPlanningIcon style={{ width: 18, height: 18, marginRight: 4 }} />
    );
  return null;
};

const GeneralInfo: React.FC<GeneralInfoProps> = ({
  data,
  onGeneralInfoChange,
  onNotificationRefresh,
  isCollapsible = false,
  isExpanded = true,
  onToggle,
  phaseName,
  showContent = true,
  eventsData: propEventsData,
  isLoadingEvents: propIsLoadingEvents,
  eventsError: propEventsError,
  parentMomentsData: propParentMomentsData,
  isLoadingParentMoments: propIsLoadingParentMoments,
  parentMomentsError: propParentMomentsError,
  isPhased = false,
  issueId,
  rootMomentName,
  rootMomentType,
  onMilestoneRefresh,
  onSeamlessRefresh,
  onRefreshNotifications
}) => {
  // ✅ CENTRALIZED: Get moment metadata from context (single source of truth)
  const { momentName: contextMomentName, momentType: contextMomentType } =
    useMomentContext();

  // Use context values, fall back to props for backward compatibility
  const momentName = contextMomentName || rootMomentName || 'Unknown';
  const momentType =
    contextMomentType || rootMomentType || data?.momentType || 'Unknown';

  // 🔍 DEBUG: Log props on mount and when they change
  React.useEffect(() => {
    console.log('🔍 GeneralInfo Moment Metadata:', {
      issueId,
      momentName,
      momentType,
      source: contextMomentName
        ? 'context'
        : rootMomentName
        ? 'props'
        : 'unknown',
      isPhased,
      phaseName,
      dataId: data?.id
    });
  }, [
    issueId,
    momentName,
    momentType,
    isPhased,
    phaseName,
    data?.id,
    contextMomentName,
    rootMomentName
  ]);
  // RBAC: Get permissions from context
  const permissions = useRBAC();
  const canEditGeneralInfo = permissions?.canEditGeneralInfo ?? false;

  const showUpdateErrorToast = (message: string, error?: unknown) => {
    if (error) {
      console.error(message, error);
    } else {
      console.error(message);
    }
    ToastQueue.negative(message, { timeout: 5000 });
  };

  // Get the appropriate fields based on layout type and moment type
  const dropdownFields = isPhased
    ? getPhasedDropdownFields()
    : getNonPhasedDropdownFields();

  // Use moment type configuration to determine which date fields to show
  // Prefer data.momentType over rootMomentType if rootMomentType is "Unknown" or invalid
  // Priority: data.momentType > rootMomentType (if not "Unknown") > "Unknown"
  const dataMomentType =
    data?.momentType ||
    (rootMomentType && rootMomentType !== 'Unknown'
      ? rootMomentType
      : 'Unknown');
  const baseDateFields = getDateFieldsByMomentType(dataMomentType, isPhased);

  // Add additional date fields that exist in the data (like effective date)
  const additionalDateFields = getAdditionalDateFields(data, isPhased);
  const dateFields = [...baseDateFields, ...additionalDateFields];

  const specialFields = isPhased
    ? getPhasedSpecialFields(momentType)
    : getNonPhasedSpecialFields();
  const activeAlertIssues = useActiveAlertIssues(issueId);
  const getGeneralInfoDateHighlightIssue = (
    fieldId: string
  ): AlertIssue | null => {
    for (const issue of activeAlertIssues) {
      if (
        matchesGeneralInfoDateAlertIssue(issue, {
          fieldId,
          phaseName
        })
      ) {
        return issue;
      }
    }
    return null;
  };

  // State for "Show on the Roadmap" toggle - initialize from API data
  const [showOnRoadmap, setShowOnRoadmap] = useState(() => {
    // For phased layout, get from phase data
    if (
      isPhased &&
      (data as any).showOnRoadmap !== undefined &&
      (data as any).showOnRoadmap !== null
    ) {
      return (data as any).showOnRoadmap;
    }
    // For non-phased layout, get from root data (if available)
    if (
      !isPhased &&
      (data as any).showOnRoadmap !== undefined &&
      (data as any).showOnRoadmap !== null
    ) {
      return (data as any).showOnRoadmap;
    }
    return false; // Default to false if not set
  });

  // State for "Inherit Milestones" toggle - initialize from API data
  const [inheritMilestones, setInheritMilestones] = useState(() => {
    // For phased layout, get from phase data
    if (
      isPhased &&
      (data as any).inheritMilestones !== undefined &&
      (data as any).inheritMilestones !== null
    ) {
      return (data as any).inheritMilestones;
    }
    // For non-phased layout, get from root data (if available)
    return false; // Default to false if not set
  });

  // State for "Mirror Release" toggle - only for non-phased when effectiveDate exists
  const [mirrorRelease, setMirrorRelease] = useState(() => {
    // Only for non-phased layout
    if (
      !isPhased &&
      (data as any).mirrorRelease !== undefined &&
      (data as any).mirrorRelease !== null
    ) {
      return (data as any).mirrorRelease;
    }
    return false; // Default to false if not set
  });

  // Secondary Date Status, derived from API data rather than held locally: the control that
  // edits it now lives in the phase status menu, so this component only reads it (for the
  // overdue colouring). A missing value reads as ACTIVE, matching creation-time defaulting.
  const secondaryDateStatus: 'ACTIVE' | 'COMPLETED' = React.useMemo(() => {
    const status = normalizeStatus((data as any).secondaryDateStatus);
    return status === 'COMPLETED' || status === 'COMPLETE'
      ? 'COMPLETED'
      : 'ACTIVE';
  }, [(data as any).secondaryDateStatus]);

  // Sync toggle states with API data changes
  useEffect(() => {
    // Update showOnRoadmap for both phased and non-phased layouts
    // ✅ FIX: Handle null values from backend - null should reset to false
    if ((data as any).showOnRoadmap !== undefined) {
      setShowOnRoadmap((data as any).showOnRoadmap === true);
    }
  }, [(data as any).showOnRoadmap, isPhased]);

  useEffect(() => {
    // ✅ FIX: Handle null values from backend - null should reset to false
    if (isPhased && (data as any).inheritMilestones !== undefined) {
      setInheritMilestones((data as any).inheritMilestones === true);
    }
  }, [(data as any).inheritMilestones, isPhased]);

  useEffect(() => {
    // ✅ FIX: Handle null values from backend - null should reset to false
    if (!isPhased && (data as any).mirrorRelease !== undefined) {
      setMirrorRelease((data as any).mirrorRelease === true);
    }
  }, [(data as any).mirrorRelease, isPhased]);

  // ✅ FIX: Monitor parent moment changes and reset inheritMilestones when parent is removed or changed
  useEffect(() => {
    const hasValidParent = (() => {
      if (!data.parentMoment) return false;

      if (typeof data.parentMoment === 'object') {
        return (
          data.parentMoment?.name &&
          data.parentMoment?.name !== 'N/A' &&
          data.parentMoment?.name !== 'none' &&
          data.parentMoment?.id &&
          data.parentMoment?.id !== 'N/A'
        );
      }

      if (typeof data.parentMoment === 'string') {
        return (
          data.parentMoment !== '' &&
          data.parentMoment !== 'N/A' &&
          data.parentMoment !== 'none' &&
          data.parentMoment !== 'NULL'
        );
      }

      return false;
    })();

    // If parent moment is removed or invalid and inheritMilestones is true, reset it
    if (!hasValidParent && inheritMilestones) {
      console.log(
        '🔄 Parent moment removed/invalid - resetting inheritMilestones to false'
      );
      setInheritMilestones(false);

      // Update backend to reflect the change
      if (issueId) {
        (async () => {
          try {
            if (isPhased && data.id) {
              const { updatePhasedField } = await import(
                '../utils/phasedFieldUpdateUtils'
              );
              await updatePhasedField(
                issueId,
                data.id,
                'inheritMilestones',
                false,
                response => {
                  console.log(
                    '✅ inheritMilestones reset to false after parent removal'
                  );
                  if (onMilestoneRefresh) {
                    onMilestoneRefresh(response);
                  }
                },
                error => {
                  showUpdateErrorToast(
                    'Failed to reset inherit milestones. Please try again.',
                    error
                  );
                  // Revert optimistic reset
                  setInheritMilestones(true);
                },
                undefined,
                phaseName
              );
            } else {
              await updateNonPhasedField(
                issueId,
                'inheritMilestones',
                false,
                response => {
                  console.log(
                    '✅ inheritMilestones reset to false after parent removal'
                  );
                  if (onMilestoneRefresh) {
                    onMilestoneRefresh(response);
                  }
                },
                error => {
                  showUpdateErrorToast(
                    'Failed to reset inherit milestones. Please try again.',
                    error
                  );
                  // Revert optimistic reset
                  setInheritMilestones(true);
                }
              );
            }
          } catch (error) {
            showUpdateErrorToast(
              'Failed to reset inherit milestones. Please try again.',
              error
            );
            // Revert optimistic reset
            setInheritMilestones(true);
          }
        })();
      }
    }
  }, [
    data.parentMoment,
    inheritMilestones,
    issueId,
    isPhased,
    data.id,
    phaseName,
    onMilestoneRefresh
  ]);

  // Helper function to determine if toggles should be shown based on moment type
  const shouldShowToggleFields = () => {
    // Use the same momentType calculation as date fields to ensure consistency
    // Priority: data.momentType > rootMomentType (if not "Unknown") > "Unknown"
    const momentType =
      data?.momentType ||
      (rootMomentType && rootMomentType !== 'Unknown'
        ? rootMomentType
        : 'Unknown');

    if (!momentType || momentType === 'Unknown') return false;

    // Use the configuration to determine if Show on Roadmap toggle should be visible
    return shouldShowRoadmapToggle(momentType);
  };

  // Whether this field owns the secondary date status (Active/Completed) control.
  // Keyed on the moment type having a secondary date at all — not on the additional-card
  // toggle's visibility or its checked state.
  const isSecondaryDateField = (fieldId: string) => {
    const effectiveMomentType =
      data?.momentType ||
      (rootMomentType && rootMomentType !== 'Unknown'
        ? rootMomentType
        : 'Unknown');

    if (!hasSecondaryDateStatus(effectiveMomentType)) return false;

    return (
      (fieldId === 'releaseDate' && shouldShowToggleFields()) ||
      (fieldId === 'effectiveDate' && shouldEnableMirrorRelease())
    );
  };

  // Helper function to determine if Inherit Milestones toggle should be shown
  // Show it for phased releases when parent moment field exists
  const shouldShowInheritMilestonesToggle = () => {
    // PCO / EOL: moment-level milestones — no inherit toggle per phase
    if (usesPcoStylePhasedDates(momentType)) {
      return false;
    }
    // Always show for phased releases (phases can inherit from parent moments)
    if (isPhased) {
      return true;
    }
    // For non-phased releases, only show if there's a valid parent moment
    if (data.parentMoment) {
      // Check if parent moment is valid (not N/A)
      if (typeof data.parentMoment === 'object') {
        return (
          data.parentMoment?.id &&
          data.parentMoment?.id !== 'N/A' &&
          data.parentMoment?.name &&
          data.parentMoment?.name !== 'N/A'
        );
      }
      if (typeof data.parentMoment === 'string') {
        return (
          data.parentMoment !== 'N/A' &&
          data.parentMoment !== 'none' &&
          data.parentMoment !== '' &&
          data.parentMoment !== 'NULL'
        );
      }
    }
    return false;
  };

  // Helper function to determine if Mirror Release toggle should be enabled
  const shouldEnableMirrorRelease = () => {
    // PCO / EOL: effectiveDate is the additional roadmap card date
    if (usesPcoStylePhasedDates(momentType)) {
      return shouldShowRoadmapToggle(
        data?.momentType ||
          (rootMomentType && rootMomentType !== 'Unknown'
            ? rootMomentType
            : 'Unknown')
      );
    }

    // Only enable for non-phased releases (effective date is always shown for non-phased)
    if (isPhased) {
      return false;
    }

    // For non-phased layouts, always show the Mirror Release toggle
    return true;
  };

  // State for status dropdown (only for non-phased layout)
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusButtonRef = useRef<HTMLButtonElement | null>(null);

  // Local state for dropdown values, initialized from props
  const [dropdownValues, setDropdownValues] = React.useState(() => {
    const values: Record<string, any> = {};
    dropdownFields.forEach(field => {
      values[field.id] = data[field.id] || '';
    });
    return values;
  });

  // State for calendar popup
  const [activeCalendar, setActiveCalendar] = React.useState<string | null>(
    null
  );

  // Local state for date values - using RSD DateComponent format
  const [dateValues, setDateValues] = React.useState(() => {
    // Helper to get the first available target date
    const getTargetDate = () => {
      if (!data?.dates) {
        return null;
      }
      // First check for actual date values - priority: targetStart > targetMonth > targetQuarter
      const dateValue =
        data.dates.targetStart ||
        data.dates.targetMonth ||
        data.dates.targetQuarter ||
        null;
      // If no date value exists, check targetDateStatus for special values (TBD/N/A)
      if (!dateValue && data.dates.targetDateStatus) {
        return data.dates.targetDateStatus;
      }
      return dateValue;
    };

    // Helper to get the first available release date
    const getReleaseDate = () => {
      if (!data?.dates) {
        return null;
      }
      // First check for actual date values - priority: releaseDate > releaseMonth > releaseQuarter
      const dateValue =
        data.dates.releaseDate ||
        data.dates.releaseMonth ||
        data.dates.releaseQuarter ||
        null;
      // If no date value exists, check releaseDateStatus for special values (TBD/N/A)
      if (!dateValue && data.dates.releaseDateStatus) {
        return data.dates.releaseDateStatus;
      }
      return dateValue;
    };

    // Helper to format date value for DateComponent
    const formatDateForComponent = (dateStr: string | null) => {
      if (!dateStr) return { type: 'special', value: 'N/A' };

      // Check for special values first
      if (dateStr === 'TBD' || dateStr === 'N/A') {
        return { type: 'special', value: dateStr };
      }

      // Check if it's a month/year format like "Oct 2025"
      if (dateStr.match(/^[A-Za-z]{3}\s\d{4}$/)) {
        return { type: 'months', months: [dateStr] };
      }

      // Check if it's a quarter format like "Q1 2025"
      if (dateStr.match(/^Q[1-4]\s\d{4}$/)) {
        return { type: 'quarters', quarters: [dateStr] };
      }

      // For ISO date format, convert to date type
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return { type: 'date', date: dateStr };
      }

      // Default to special value for unknown formats
      return { type: 'special', value: 'N/A' };
    };

    const targetDate = getTargetDate();
    const releaseDate = getReleaseDate();
    // For effective date, check actual date first, then effectiveDateStatus
    const effectiveDateValue =
      data.dates?.effectiveDate ||
      data.dates?.effectiveMonth ||
      data.dates?.effectiveQuarter ||
      null;
    const effectiveDate =
      effectiveDateValue || data.dates?.effectiveDateStatus || null;

    const customerCommDateValue = isSpecialDateStatus(
      data.dates?.customerNotificationDateStatus
    )
      ? null
      : data.dates?.customerNotificationDate ||
        data.dates?.customerNotificationMonth ||
        data.dates?.customerNotificationQuarter ||
        null;
    const customerNotificationDate =
      customerCommDateValue ||
      data.dates?.customerNotificationDateStatus ||
      null;

    return {
      marketingMoment: formatDateForComponent(targetDate),
      releaseDate: formatDateForComponent(releaseDate),
      effectiveDate: formatDateForComponent(effectiveDate),
      customerNotificationDate: formatDateForComponent(customerNotificationDate)
    };
  });

  // Helper function to extract comparable date value from DateComponent format
  const getComparableDate = (dateValue: any): string => {
    if (!dateValue) return '';

    // Handle direct string values (like "Oct 2025")
    if (typeof dateValue === 'string') {
      return dateValue.trim();
    }

    // Handle DateComponent format
    if (typeof dateValue === 'object') {
      if (dateValue.type === 'months' && dateValue.months?.length > 0) {
        return dateValue.months[0].trim();
      }
      if (dateValue.type === 'quarters' && dateValue.quarters?.length > 0) {
        return dateValue.quarters[0].trim();
      }
      if (dateValue.type === 'date' && dateValue.date) {
        return dateValue.date.trim();
      }
      if (dateValue.type === 'special') {
        return dateValue.value || 'N/A'; // Return the special value for comparison
      }
      // Legacy format support
      if (dateValue.start) {
        return dateValue.start.trim();
      }
    }

    return '';
  };

  /** While a date PATCH is in flight, block prop sync from overwriting optimistic dateValues. */
  const pendingDateFieldsRef = React.useRef(new Set<string>());

  // Function to check if dates are different for toggle enabling
  // For phased layouts: compare target date vs release date (for showOnRoadmap toggle)
  // For non-phased layouts: compare effective date vs target/release date (for mirrorRelease toggle)
  // Returns true if toggle should be disabled for turning ON (but not for turning OFF)
  const areDatesEqual = (): boolean => {
    if (isPhased) {
      // PCO / EOL: compare customerNotificationDate vs effectiveDate
      if (usesPcoStylePhasedDates(momentType)) {
        const custNotifDate = getComparableDate(
          dateValues.customerNotificationDate
        );
        const effectDate = getComparableDate(dateValues.effectiveDate);

        const isCustCommSpecial =
          !custNotifDate ||
          custNotifDate === 'N/A' ||
          custNotifDate === 'TBD' ||
          data?.dates?.customerNotificationDateStatus === 'TBD' ||
          data?.dates?.customerNotificationDateStatus === 'N/A';
        const isEffectSpecial =
          !effectDate ||
          effectDate === 'N/A' ||
          effectDate === 'TBD' ||
          data?.dates?.effectiveDateStatus === 'TBD' ||
          data?.dates?.effectiveDateStatus === 'N/A';

        // Disable if either date is missing/special
        if (isCustCommSpecial || isEffectSpecial) return true;
        // Disable if both dates are the same
        if (custNotifDate === effectDate) return true;
        // Enable when both dates exist AND are different
        return false;
      }

      // Phased layout: Compare target date with release date for showOnRoadmap toggle
      const targetDate = getComparableDate(dateValues.marketingMoment);
      const releaseDate = getComparableDate(dateValues.releaseDate);

      // Check if either date is N/A, TBD, or empty
      // Also check the new separate date status fields
      const isTargetSpecial =
        !targetDate ||
        targetDate === 'N/A' ||
        targetDate === 'TBD' ||
        data?.dates?.targetDateStatus === 'TBD' ||
        data?.dates?.targetDateStatus === 'N/A';
      const isReleaseSpecial =
        !releaseDate ||
        releaseDate === 'N/A' ||
        releaseDate === 'TBD' ||
        data?.dates?.releaseDateStatus === 'TBD' ||
        data?.dates?.releaseDateStatus === 'N/A';

      // DISABLE toggle if EITHER date is N/A, TBD, or empty
      if (isTargetSpecial || isReleaseSpecial) {
        return true;
      }

      // Also disable if both dates are the same
      if (targetDate === releaseDate) {
        return true;
      }

      // Enable toggle only when both dates exist AND are different
      return false;
    } else {
      // Non-phased layout: Compare effective date with target date AND release date for mirrorRelease toggle
      const effectiveDate = getComparableDate(dateValues.effectiveDate);
      const targetDate = getComparableDate(dateValues.marketingMoment);
      const releaseDate = getComparableDate(dateValues.releaseDate);

      // Check if effective date is N/A, TBD, or empty
      // Also check the new separate date status fields
      const isEffectiveSpecial =
        !effectiveDate ||
        effectiveDate === 'N/A' ||
        effectiveDate === 'TBD' ||
        data?.dates?.effectiveDateStatus === 'TBD' ||
        data?.dates?.effectiveDateStatus === 'N/A';

      // DISABLE toggle if effective date is N/A, TBD, or empty
      if (isEffectiveSpecial) {
        return true;
      }

      // Check if target or release dates are valid
      const isTargetSpecial =
        !targetDate ||
        targetDate === 'N/A' ||
        targetDate === 'TBD' ||
        data?.dates?.targetDateStatus === 'TBD' ||
        data?.dates?.targetDateStatus === 'N/A';
      const isReleaseSpecial =
        !releaseDate ||
        releaseDate === 'N/A' ||
        releaseDate === 'TBD' ||
        data?.dates?.releaseDateStatus === 'TBD' ||
        data?.dates?.releaseDateStatus === 'N/A';

      // If both target and release are invalid/empty, disable toggle
      if (isTargetSpecial && isReleaseSpecial) {
        return true;
      }

      // Check if ANY two dates are equal
      // Disable toggle if any pair of dates match
      const matchesTarget = targetDate && effectiveDate === targetDate;
      const matchesRelease = releaseDate && effectiveDate === releaseDate;
      const targetMatchesRelease =
        targetDate && releaseDate && targetDate === releaseDate;

      // Disable if effective matches target
      if (matchesTarget) {
        return true;
      }

      // Disable if effective matches release
      if (matchesRelease) {
        return true;
      }

      // Disable if target matches release
      if (targetMatchesRelease) {
        return true;
      }

      // Enable toggle when all three dates are different and valid
      return false;
    }
  };

  // Check if toggle should be disabled
  // Note: This only prevents toggling ON, users can always toggle OFF
  // Use useMemo to recalculate when dateValues change
  // Also consider RBAC permissions - disable if user cannot edit
  const isToggleDisabled = useMemo(() => {
    // If user cannot edit, disable toggle completely
    if (!canEditGeneralInfo) {
      return true;
    }
    // Otherwise, disable only if dates are equal
    return areDatesEqual();
  }, [
    dateValues.marketingMoment,
    dateValues.releaseDate,
    dateValues.effectiveDate,
    dateValues.customerNotificationDate,
    isPhased,
    canEditGeneralInfo
  ]);

  // Helper function to display date value as text
  const getDisplayDateValue = (dateValue: any): string => {
    if (!dateValue) return 'N/A';

    // Handle DateComponent format
    if (typeof dateValue === 'object') {
      if (dateValue.type === 'months' && dateValue.months?.length > 0) {
        return dateValue.months[0];
      }
      if (dateValue.type === 'quarters' && dateValue.quarters?.length > 0) {
        return dateValue.quarters[0];
      }
      if (dateValue.type === 'date' && dateValue.date) {
        // Format date for display
        try {
          const date = new Date(dateValue.date);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch {
          return dateValue.date;
        }
      }
      if (dateValue.type === 'special') {
        return dateValue.value || 'N/A';
      }
    }

    // Handle direct string values
    if (typeof dateValue === 'string') {
      return dateValue;
    }

    return 'N/A';
  };

  // Get current date value for CalendarRange by field ID
  const getCurrentDateValueForField = (fieldId: string) => {
    const currentValue = dateValues[fieldId];
    if (!currentValue) return undefined;

    // Convert DateComponent format to CalendarDate for CalendarRange
    if (currentValue.type === 'date' && currentValue.date) {
      try {
        return parseDate(currentValue.date);
      } catch {
        return undefined;
      }
    }

    return undefined;
  };

  // Get current special value for CalendarRange by field ID (matches DateComponent)
  const getCurrentSpecialValueForField = (
    fieldId: string
  ): 'TBD' | 'N/A' | null => {
    const currentValue = dateValues[fieldId];
    if (!currentValue) return 'N/A';

    if (currentValue.type === 'special') {
      return currentValue.value as 'TBD' | 'N/A';
    }

    return null; // Return null if it's not a special value (date/month/quarter)
  };

  // Handle calendar selection for specific field
  const handleCalendarSelectionForField = (
    fieldId: string,
    date: any,
    isQuarterSelection?: boolean,
    selectedQuarter?: { quarter: number; year: number },
    isMonthSelection?: boolean,
    selectedMonth?: { month: string; year: number },
    specialValue?: 'TBD' | 'N/A' | null
  ) => {
    let newValue;

    // If specialValue is explicitly null (TBD/N/A unchecked), do nothing
    // Keep calendar open for user to select a valid date
    if (specialValue === null) {
      return; // Don't close calendar, don't update value
    }

    if (specialValue) {
      newValue = { type: 'special', value: specialValue };
    } else if (isMonthSelection && selectedMonth) {
      newValue = {
        type: 'months',
        months: [`${selectedMonth.month} ${selectedMonth.year}`]
      };
    } else if (isQuarterSelection && selectedQuarter) {
      newValue = {
        type: 'quarters',
        quarters: [`Q${selectedQuarter.quarter} ${selectedQuarter.year}`]
      };
    } else if (date) {
      newValue = { type: 'date', date: date.toString() };
    } else {
      return; // No valid selection
    }

    // Update the date value for the specific field
    handleDateChange(fieldId, newValue);
  };

  // Handler for show on roadmap toggle change
  const handleShowOnRoadmapChange = async (
    newValue: boolean,
    options?: { suppressSeamlessRefresh?: boolean }
  ) => {
    // 1. Prevent turning ON if dates are invalid (but allow turning OFF)
    if (newValue && isToggleDisabled) {
      return; // Block the change
    }

    // 2. ✅ Capture OLD value BEFORE change
    const oldValue = showOnRoadmap;

    setShowOnRoadmap(newValue);

    // Update backend with showOnRoadmap value
    if (issueId) {
      try {
        if (isPhased && data.id) {
          if (usesPcoStylePhasedDates(momentType)) {
            // PCO / EOL stages: PATCH /moments/{issueId}/rtm-phases/{phaseId}
            const patchResponse = await patchRtmPhase(issueId, data.id, {
              showOnRoadmap: newValue
            });

            if (!options?.suppressSeamlessRefresh && onSeamlessRefresh)
              onSeamlessRefresh(patchResponse);
            if (onNotificationRefresh) onNotificationRefresh(patchResponse);
          } else {
            // All other phased layouts - use phased field update utility
            const { updatePhasedField } = await import(
              '../utils/phasedFieldUpdateUtils'
            );
            await updatePhasedField(
              issueId,
              data.id, // phaseId
              'showOnRoadmap',
              newValue,
              response => {
                if (!options?.suppressSeamlessRefresh && onSeamlessRefresh)
                  onSeamlessRefresh(response);
                if (onNotificationRefresh) onNotificationRefresh(response);
              },
              error => {
                showUpdateErrorToast(
                  'Failed to update Show on Roadmap. Please try again.',
                  error
                );
                setShowOnRoadmap(!newValue);
              },
              undefined, // currentPhaseData
              phaseName
            );
          }
        } else {
          // Non-phased layout - use non-phased field update utility
          await updateNonPhasedField(
            issueId,
            'showOnRoadmap',
            newValue,
            response => {
              if (!options?.suppressSeamlessRefresh && onSeamlessRefresh)
                onSeamlessRefresh(response);
              if (onNotificationRefresh) onNotificationRefresh(response);
            },
            error => {
              showUpdateErrorToast(
                'Failed to update Show on Roadmap. Please try again.',
                error
              );
              // Revert the state on error
              setShowOnRoadmap(!newValue);
            }
          );
        }
      } catch (error) {
        showUpdateErrorToast(
          'Failed to update Show on Roadmap. Please try again.',
          error
        );
        // Revert the state on error
        setShowOnRoadmap(!newValue);
      }
    }
  };

  // Handler for inherit milestones toggle change
  const handleInheritMilestonesChange = async (newValue: boolean) => {
    // ✅ FIX: Removed one-way toggle restriction - users can now turn off inheritance
    // This allows users to switch parents without getting stuck

    // Additional validation: prevent enabling if parent moment is N/A
    if (newValue && data.parentMoment) {
      const isParentNA =
        (typeof data.parentMoment === 'object' &&
          (data.parentMoment?.name === 'N/A' ||
            data.parentMoment?.id === 'N/A')) ||
        (typeof data.parentMoment === 'string' && data.parentMoment === 'N/A');

      if (isParentNA) {
        return;
      }
    }

    // 1. ✅ Capture OLD value BEFORE change
    const oldValue = inheritMilestones;

    setInheritMilestones(newValue);

    // Update backend with inheritMilestones value
    if (issueId) {
      try {
        if (isPhased && data.id) {
          // Phased layout - use phased field update utility
          const { updatePhasedField } = await import(
            '../utils/phasedFieldUpdateUtils'
          );
          await updatePhasedField(
            issueId,
            data.id, // phaseId
            'inheritMilestones',
            newValue,
            response => {
              // ✅ FIX: Trigger soft refresh to show/hide inherited milestones instantly
              if (onMilestoneRefresh) {
                onMilestoneRefresh(response);
              }
            },
            error => {
              showUpdateErrorToast(
                'Failed to update inherit milestones. Please try again.',
                error
              );
              // Revert the state on error
              setInheritMilestones(!newValue);
            },
            undefined, // currentPhaseData
            phaseName
          );
        } else {
          // Non-phased layout - use non-phased field update utility
          await updateNonPhasedField(
            issueId,
            'inheritMilestones',
            newValue,
            response => {
              // ✅ FIX: Trigger soft refresh to show/hide inherited milestones instantly
              if (onMilestoneRefresh) {
                onMilestoneRefresh(response);
              }
            },
            error => {
              showUpdateErrorToast(
                'Failed to update inherit milestones. Please try again.',
                error
              );
              // Revert the state on error
              setInheritMilestones(!newValue);
            }
          );
        }
      } catch (error) {
        showUpdateErrorToast(
          'Failed to update inherit milestones. Please try again.',
          error
        );
        // Revert the state on error
        setInheritMilestones(!newValue);
      }
    }
  };

  // Handler for Show on Roadmap toggle change (for Offer, Price Change, or Program type)
  const handleMirrorReleaseChange = async (newValue: boolean) => {
    // 1. Prevent turning ON if dates are invalid (but allow turning OFF)
    if (newValue && isToggleDisabled) {
      return; // Block the change
    }

    setMirrorRelease(newValue);

    // Update backend with showOnRoadmap value (renamed from mirrorRelease)
    if (issueId) {
      try {
        // Only for non-phased layout - use non-phased field update utility
        await updateNonPhasedField(
          issueId,
          'showOnRoadmap', // Changed from 'mirrorRelease' to 'showOnRoadmap'
          newValue,
          response => {},
          error => {
            showUpdateErrorToast(
              'Failed to update Show on Roadmap. Please try again.',
              error
            );
            // Revert the state on error
            setMirrorRelease(!newValue);
          }
        );
      } catch (error) {
        showUpdateErrorToast(
          'Failed to update Show on Roadmap. Please try again.',
          error
        );
        // Revert the state on error
        setMirrorRelease(!newValue);
      }
    } else {
      console.warn('⚠️ No issueId available for showOnRoadmap update');
    }
  };

  /**
   * Sync showOnRoadmap / mirrorRelease after the date PATCH succeeds.
   * Must run after the date save — calling handleShowOnRoadmapChange earlier triggers
   * onSeamlessRefresh with stale moment data and reverts the date field in the UI.
   */
  const applyToggleSideEffectsForSavedDate = async (
    changedField: 'marketingMoment' | 'releaseDate' | 'effectiveDate',
    value: any,
    dateStr: string
  ) => {
    if (!issueId) return;

    const syncShowOnRoadmapOff = async () => {
      if (showOnRoadmap !== false) {
        setShowOnRoadmap(false);
      }
      await handleShowOnRoadmapChange(false, {
        // Auto-toggle side effects run immediately after a date save.
        // Suppress full seamless refresh here to avoid stale toggle PATCH payloads
        // from overwriting the optimistic date value in the UI.
        suppressSeamlessRefresh: true
      });
    };

    if (dateStr === 'N/A' || dateStr === 'TBD') {
      if (isPhased) {
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);
        if (useShowOnRoadmap && showOnRoadmap !== false) {
          await syncShowOnRoadmapOff();
        } else if (useShowOnRoadmap && onRefreshNotifications) {
          onRefreshNotifications();
        }
      } else {
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);
        if (useShowOnRoadmap && showOnRoadmap !== false) {
          await syncShowOnRoadmapOff();
        } else if (useShowOnRoadmap && onRefreshNotifications) {
          onRefreshNotifications();
        } else if (!useShowOnRoadmap && mirrorRelease !== false) {
          setMirrorRelease(false);
          await handleMirrorReleaseChange(false);
        } else if (!useShowOnRoadmap && onRefreshNotifications) {
          onRefreshNotifications();
        }
      }
      return;
    }

    const newComparable = getComparableDate(value);

    if (changedField === 'marketingMoment') {
      const currentReleaseDateComparable = getComparableDate(
        dateValues.releaseDate
      );
      const currentEffectiveDateComparable = getComparableDate(
        dateValues.effectiveDate
      );

      if (
        isPhased &&
        newComparable &&
        currentReleaseDateComparable &&
        newComparable === currentReleaseDateComparable &&
        shouldShowRoadmapToggle(momentType)
      ) {
        await syncShowOnRoadmapOff();
        return;
      }

      if (!isPhased) {
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);
        const targetMatchesEffective =
          currentEffectiveDateComparable &&
          newComparable === currentEffectiveDateComparable;
        const targetMatchesRelease =
          currentReleaseDateComparable &&
          newComparable === currentReleaseDateComparable;

        if (
          useShowOnRoadmap &&
          (targetMatchesEffective || targetMatchesRelease)
        ) {
          await syncShowOnRoadmapOff();
        } else if (
          !useShowOnRoadmap &&
          (targetMatchesEffective || targetMatchesRelease) &&
          mirrorRelease
        ) {
          setMirrorRelease(false);
          await handleMirrorReleaseChange(false);
        }
      }
      return;
    }

    if (changedField === 'releaseDate') {
      const currentTargetDateComparable = getComparableDate(
        dateValues.marketingMoment
      );

      if (
        isPhased &&
        newComparable &&
        currentTargetDateComparable &&
        newComparable === currentTargetDateComparable &&
        shouldShowRoadmapToggle(momentType)
      ) {
        await syncShowOnRoadmapOff();
        return;
      }

      if (!isPhased) {
        const currentEffectiveDateComparable = getComparableDate(
          dateValues.effectiveDate
        );
        const releaseMatchesEffective =
          currentEffectiveDateComparable &&
          newComparable === currentEffectiveDateComparable;
        const releaseMatchesTarget =
          currentTargetDateComparable &&
          newComparable === currentTargetDateComparable;
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        if (
          useShowOnRoadmap &&
          (releaseMatchesTarget || releaseMatchesEffective) &&
          showOnRoadmap
        ) {
          await syncShowOnRoadmapOff();
        } else if (
          !useShowOnRoadmap &&
          (releaseMatchesEffective || releaseMatchesTarget) &&
          mirrorRelease
        ) {
          setMirrorRelease(false);
          await handleMirrorReleaseChange(false);
        }
      }
      return;
    }

    if (changedField === 'effectiveDate') {
      const currentTargetDateComparable = getComparableDate(
        dateValues.marketingMoment
      );
      const currentReleaseDateComparable = getComparableDate(
        dateValues.releaseDate
      );

      if (isPhased && newComparable) {
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        if (usesPcoStylePhasedDates(momentType)) {
          const currentCustCommComparable = getComparableDate(
            dateValues.customerNotificationDate
          );
          if (
            useShowOnRoadmap &&
            currentCustCommComparable &&
            newComparable === currentCustCommComparable
          ) {
            // The date PATCH derives this flag and records the system audit.
            setShowOnRoadmap(false);
          }
          return;
        }

        if (
          useShowOnRoadmap &&
          ((currentTargetDateComparable &&
            newComparable === currentTargetDateComparable) ||
            (currentReleaseDateComparable &&
              newComparable === currentReleaseDateComparable))
        ) {
          await syncShowOnRoadmapOff();
        }
        return;
      }

      if (!isPhased && newComparable) {
        const effectiveMatchesTarget =
          currentTargetDateComparable &&
          newComparable === currentTargetDateComparable;
        const effectiveMatchesRelease =
          currentReleaseDateComparable &&
          newComparable === currentReleaseDateComparable;
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        if (
          useShowOnRoadmap &&
          (effectiveMatchesTarget || effectiveMatchesRelease)
        ) {
          await syncShowOnRoadmapOff();
        } else if (
          !useShowOnRoadmap &&
          (effectiveMatchesTarget || effectiveMatchesRelease) &&
          mirrorRelease
        ) {
          setMirrorRelease(false);
          await handleMirrorReleaseChange(false);
        }
      }
    }
  };

  // Use events data from props (passed from parent) or fallback to local state for backward compatibility
  const eventsData = propEventsData || mockDropdownOptions.events;
  const isLoadingEvents = propIsLoadingEvents || false;
  const eventsError = propEventsError || null;

  // Use parent moments data from props (passed from parent) or fallback to local state for backward compatibility
  const parentMomentsData =
    propParentMomentsData || mockDropdownOptions.parentMoments;
  const isLoadingParentMoments = propIsLoadingParentMoments || false;
  const parentMomentsError = propParentMomentsError || null;

  // Update dateValues when data prop changes (important for async data loading)
  React.useEffect(() => {
    // Helper to get the first available target date
    const getTargetDate = () => {
      if (!data.dates) return null;
      // First check for actual date values
      const dateValue =
        data.dates.targetStart ||
        data.dates.targetMonth ||
        data.dates.targetQuarter ||
        null;
      // If no date value exists, check targetDateStatus for special values (TBD/N/A)
      if (!dateValue && data.dates.targetDateStatus) {
        return data.dates.targetDateStatus;
      }
      return dateValue;
    };

    // Helper to get the first available release date
    const getReleaseDate = () => {
      if (!data.dates) return null;
      // First check for actual date values
      const dateValue =
        data.dates.releaseDate ||
        data.dates.releaseMonth ||
        data.dates.releaseQuarter ||
        null;
      // If no date value exists, check releaseDateStatus for special values (TBD/N/A)
      if (!dateValue && data.dates.releaseDateStatus) {
        return data.dates.releaseDateStatus;
      }
      return dateValue;
    };

    // Helper to format date value for DateComponent
    const formatDateForComponent = (dateStr: string | null) => {
      if (!dateStr) return { type: 'special', value: 'N/A' };

      // Check for special values first
      if (dateStr === 'TBD' || dateStr === 'N/A') {
        return { type: 'special', value: dateStr };
      }

      // Check if it's a month/year format like "Oct 2025"
      if (dateStr.match(/^[A-Za-z]{3}\s\d{4}$/)) {
        return { type: 'months', months: [dateStr] };
      }

      // Check if it's a quarter format like "Q1 2025"
      if (dateStr.match(/^Q[1-4]\s\d{4}$/)) {
        return { type: 'quarters', quarters: [dateStr] };
      }

      // For ISO date format, convert to date type
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return { type: 'date', date: dateStr };
      }

      // Default to special value for unknown formats
      return { type: 'special', value: 'N/A' };
    };

    const targetDate = getTargetDate();
    const releaseDate = getReleaseDate();

    // Helper to get the first available effective date
    const getEffectiveDate = () => {
      if (!data?.dates) return null;
      // First check for actual date values - priority: effectiveDate > effectiveMonth > effectiveQuarter
      const dateValue =
        data.dates.effectiveDate ||
        data.dates.effectiveMonth ||
        data.dates.effectiveQuarter ||
        null;
      // If no date value exists, check effectiveDateStatus for special values (TBD/N/A)
      if (!dateValue && data.dates.effectiveDateStatus) {
        return data.dates.effectiveDateStatus;
      }
      return dateValue;
    };

    const effectiveDate = getEffectiveDate();

    const getCustomerNotificationDate = () => {
      if (!data?.dates) return null;
      if (isSpecialDateStatus(data.dates.customerNotificationDateStatus)) {
        return data.dates.customerNotificationDateStatus ?? null;
      }
      const dateValue =
        data.dates.customerNotificationDate ||
        data.dates.customerNotificationMonth ||
        data.dates.customerNotificationQuarter ||
        null;
      if (!dateValue && data.dates.customerNotificationDateStatus) {
        return data.dates.customerNotificationDateStatus;
      }
      return dateValue;
    };

    const customerNotificationDate = getCustomerNotificationDate();

    const formattedValues = {
      marketingMoment: formatDateForComponent(targetDate),
      releaseDate: formatDateForComponent(releaseDate),
      effectiveDate: formatDateForComponent(effectiveDate),
      customerNotificationDate: formatDateForComponent(customerNotificationDate)
    };
    setDateValues(prev => {
      const pending = pendingDateFieldsRef.current;
      if (pending.size === 0) {
        return formattedValues;
      }
      const next = { ...formattedValues };
      (
        [
          'marketingMoment',
          'releaseDate',
          'effectiveDate',
          'customerNotificationDate'
        ] as const
      ).forEach(field => {
        if (pending.has(field) && prev[field]) {
          next[field] = prev[field];
        }
      });
      return next;
    });
  }, [data?.dates, data]); // Re-run when dates data changes - also depend on data to catch when data object changes

  // Add proper handling for empty events list
  const effectiveEventsData = React.useMemo(() => {
    if (isLoadingEvents) {
      return [{ id: 'loading', name: 'Loading events...' }];
    }

    if (eventsData && eventsData.length > 0) {
      return eventsData;
    }

    // Graceful fallback for empty list
    return [
      { id: 'none', name: 'No events available' },
      ...mockDropdownOptions.events
    ];
  }, [eventsData, isLoadingEvents]);

  // Add proper handling for empty parent moments list
  const effectiveParentMomentsData = React.useMemo(() => {
    if (isLoadingParentMoments) {
      return [{ id: 'loading', name: 'Loading parent moments...' }];
    }

    if (parentMomentsData && parentMomentsData.length > 0) {
      return parentMomentsData;
    }

    // Graceful fallback for empty list
    return [
      { id: 'none', name: 'No parent moments available' },
      ...mockDropdownOptions.parentMoments
    ];
  }, [parentMomentsData, isLoadingParentMoments]);

  // Risk level to status mapping
  const mapRiskLevelToStatus = (riskLevel: string): string => {
    const riskStatusMap: Record<string, string> = {
      [RISK_LEVELS.LOW]: STATUS_VALUES.ACTIVE, // Green -> ACTIVE
      [RISK_LEVELS.MODERATE]: STATUS_VALUES.ACTIVE, // Yellow -> ACTIVE
      [RISK_LEVELS.HIGH]: STATUS_VALUES.ACTIVE, // Red -> ACTIVE
      [RISK_LEVELS.NA]: STATUS_VALUES.COMPLETE // NA -> COMPLETE
    };
    return riskStatusMap[riskLevel] || STATUS_VALUES.ACTIVE;
  };

  // Get current risk level for status display
  const getCurrentStatus = () => {
    // Show risk level directly in status field
    if (data.riskLevel) {
      return data.riskLevel;
    }
    return RISK_LEVELS.LOW; // Default to Green
  };

  // Get risk level icon based on current status
  const getRiskLevelIcon = (riskLevel?: string) => {
    switch (riskLevel?.toLowerCase()) {
      case 'green':
      case RISK_LEVELS.LOW.toLowerCase():
        return RiskLevelLowIcon;
      case 'yellow':
      case RISK_LEVELS.MODERATE.toLowerCase():
        return RiskLevelModerateIcon;
      case 'red':
      case RISK_LEVELS.HIGH.toLowerCase():
        return RiskLevelHighIcon;
      case 'planning':
      case RISK_LEVELS.IN_PLANNING.toLowerCase():
      case 'in_planning':
        return RiskLevelPlanningIcon;
      case 'na':
      case RISK_LEVELS.NA.toLowerCase():
        return RiskLevelLowIcon; // Use low icon for NA
      default:
        return RiskLevelLowIcon; // Default to low if no risk level
    }
  };

  // Status dropdown helper functions (show risk level values in Figma format)
  const getStatusDisplayText = (status: string) => {
    // Map risk level values to display text in "Risk Level: ..." format
    const statusMap: Record<string, string> = {
      [RISK_LEVELS.LOW]: 'Risk Level: Low',
      [RISK_LEVELS.MODERATE]: 'Risk Level: Moderate',
      [RISK_LEVELS.HIGH]: 'Risk Level: High',
      [RISK_LEVELS.IN_PLANNING]: 'Risk Level: In Planning',
      [RISK_LEVELS.NA]: 'Risk Level: NA'
    };
    return statusMap[status] || status || 'Risk Level: Low';
  };

  const getStatusBadgeClass = (status: string) => {
    // Use risk level badge styles
    switch (status) {
      case RISK_LEVELS.LOW: // Green
        return slideoutStyles.riskLevelBadgeDropdown; // Will be styled green
      case RISK_LEVELS.MODERATE: // Yellow
        return slideoutStyles.riskLevelBadgeDropdown; // Will be styled yellow
      case RISK_LEVELS.HIGH: // Red
        return slideoutStyles.riskLevelBadgeDropdown; // Will be styled red
      case RISK_LEVELS.NA: // NA
        return slideoutStyles.riskLevelBadgeDropdown; // Will be styled gray
      default:
        return slideoutStyles.riskLevelBadgeDropdown;
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    // 1. Capture OLD value FIRST (before API call)
    const oldRiskLevel = data?.riskLevel || '';

    // Map display values to backend values (same as other risk level dropdowns)
    const riskLevelMap: Record<string, string> = {
      low: RISK_LEVELS.LOW, // 'low' -> 'Green'
      moderate: RISK_LEVELS.MODERATE, // 'moderate' -> 'Yellow'
      high: RISK_LEVELS.HIGH, // 'high' -> 'Red'
      in_planning: RISK_LEVELS.IN_PLANNING, // 'in_planning' -> 'In Planning'
      na: RISK_LEVELS.NA // 'na' -> 'NA'
    };

    // Handle both lowercase and display names
    const normalizedStatus = newStatus.toLowerCase();
    const mappedRiskLevel =
      riskLevelMap[normalizedStatus] ||
      riskLevelMap[newStatus.toLowerCase().replace(/risk level: /, '')] ||
      newStatus; // fallback to original value

    // Update parent component state immediately for instant UI feedback
    if (onGeneralInfoChange) {
      onGeneralInfoChange({
        ...data,
        riskLevel: mappedRiskLevel
      });
    }

    setStatusDropdownOpen(false);

    // 2. Update via API using the appropriate utility for phased vs non-phased
    if (issueId) {
      try {
        if (isPhased && data.id) {
          // Phased layout - use phased field update utility for risk level
          const { updatePhasedRiskLevelField } = await import(
            '../utils/phasedFieldUpdateUtils'
          );
          await updatePhasedRiskLevelField(
            issueId,
            data.id, // phaseId
            mappedRiskLevel, // Use mapped value
            response => {},
            error => {
              console.error(`❌ Failed to update phased risk level:`, error);
              // Revert local state on error
              if (onGeneralInfoChange) {
                onGeneralInfoChange({
                  ...data,
                  riskLevel: oldRiskLevel
                });
              }
            },
            phaseName
          );
        } else {
          // Non-phased layout - use non-phased field update utility
          await updateNonPhasedField(
            issueId,
            'riskLevel',
            mappedRiskLevel, // Use mapped value instead of newStatus
            response => {},
            error => {
              console.error(
                `❌ Failed to update non-phased risk level:`,
                error
              );
              // Revert local state on error
              if (onGeneralInfoChange) {
                onGeneralInfoChange({
                  ...data,
                  riskLevel: oldRiskLevel
                });
              }
            }
          );
        }
      } catch (error) {
        console.error(`❌ Error updating risk level:`, error);
        // Revert local state on error
        if (onGeneralInfoChange) {
          onGeneralInfoChange({
            ...data,
            riskLevel: oldRiskLevel
          });
        }
      }
    }
  };

  // Status dropdown portal component
  const StatusDropdownPortal = () => {
    const statusOptions = [
      { id: RISK_LEVELS.LOW, name: 'Risk Level: Low' },
      { id: RISK_LEVELS.MODERATE, name: 'Risk Level: Moderate' },
      { id: RISK_LEVELS.HIGH, name: 'Risk Level: High' },
      { id: RISK_LEVELS.IN_PLANNING, name: 'In Planning' }
    ];

    if (!statusButtonRef.current) return null;

    const buttonRect = statusButtonRef.current.getBoundingClientRect();
    const dropdownStyle = {
      position: 'fixed' as const,
      top: buttonRect.bottom + 4,
      left: buttonRect.left,
      zIndex: 100000, // Below header (100001) but above content
      pointerEvents: 'auto' as const
    };

    return createPortal(
      <div
        className={slideoutStyles.dropdownPortal}
        style={{
          ...dropdownStyle,
          backgroundColor: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          borderRadius: '4px'
        }}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <div
          className={slideoutStyles.riskLevelDropdownMenu}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {statusOptions.map(option => (
            <div
              key={option.id}
              className={slideoutStyles.riskLevelMenuItem}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                handleStatusChange(option.id);
              }}
            >
              <div className={slideoutStyles.riskLevelMenuItemContent}>
                <div className={slideoutStyles.riskIconContainer}>
                  {option.id === RISK_LEVELS.IN_PLANNING ? (
                    <img
                      src={RiskLevelPlanningIcon}
                      alt="In Planning"
                      className={slideoutStyles.riskLevelIcon}
                    />
                  ) : (
                    <div
                      className={`${slideoutStyles.riskIcon} ${
                        option.id === RISK_LEVELS.LOW
                          ? slideoutStyles.riskIconLow
                          : option.id === RISK_LEVELS.MODERATE
                          ? slideoutStyles.riskIconModerate
                          : slideoutStyles.riskIconHigh
                      }`}
                    />
                  )}
                </div>
                <span>{option.name}</span>
              </div>
              {getCurrentStatus() === option.id && (
                <div className={slideoutStyles.checkmark}>
                  <BlackCheckMarkIcon />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>,
      document.body
    );
  };

  React.useEffect(() => {
    // Sync dropdown state if data prop changes
    setDropdownValues(() => {
      const values: Record<string, any> = {};
      dropdownFields.forEach(field => {
        values[field.id] = data[field.id] || '';
      });
      return values;
    });
  }, [data]); // Removed dropdownFields from deps as it's stable

  const handleDropdownChange = async (id: string, newName: string) => {
    console.log('🎯 handleDropdownChange called:', {
      id,
      newName,
      isPhased,
      issueId
    });

    // 1. ✅ Capture OLD value FIRST (before any changes)
    const oldValue =
      typeof data?.[id] === 'object'
        ? (data[id] as any)?.name || (data[id] as any)?.id || ''
        : data?.[id] || '';

    const options =
      mockDropdownOptions[
        dropdownFields.find(f => f.id === id)?.optionsKey || ''
      ];
    const newId = getOptionIdByName(options, newName);

    // Update local state immediately for instant UI feedback
    setDropdownValues(prev => ({ ...prev, [id]: newId }));

    // Update parent component state immediately for instant UI feedback
    if (onGeneralInfoChange) {
      onGeneralInfoChange({ ...data, [id]: newId });
    }

    // Update via API using the appropriate utility for phased vs non-phased
    if (issueId) {
      try {
        if (isPhased && data.id) {
          // Phased layout - use phased field update utility
          const { updatePhasedField } = await import(
            '../utils/phasedFieldUpdateUtils'
          );
          await updatePhasedField(
            issueId,
            data.id, // phaseId
            id,
            newId,
            response => {
              // Trigger milestone refresh on tier change
              if (id === 'tier' && onMilestoneRefresh) {
                onMilestoneRefresh(response);
              }
            },
            error => {
              console.error(`❌ Failed to update phased field '${id}':`, error);
              // Revert local state on error
              setDropdownValues(prev => ({ ...prev, [id]: data[id] || '' }));
              if (onGeneralInfoChange) {
                onGeneralInfoChange({ ...data, [id]: data[id] || '' });
              }
            },
            undefined, // currentPhaseData
            phaseName
          );
        } else {
          // Non-phased layout - use specialized functions for complex fields
          if (id === 'event') {
            const options =
              mockDropdownOptions[
                dropdownFields.find(f => f.id === id)?.optionsKey || ''
              ];
            const selectedOption = options.find(opt => opt.name === newName);
            if (selectedOption) {
              console.log('🔄 Updating non-phased event field:', {
                issueId,
                fieldData: { id: selectedOption.id, name: selectedOption.name },
                oldValue,
                newValue: newName
              });

              const fieldData = {
                id: selectedOption.id,
                name: selectedOption.name
              };
              await updateEventField(
                issueId,
                fieldData,
                response => {
                  if (onSeamlessRefresh) onSeamlessRefresh(response);
                  if (onNotificationRefresh) onNotificationRefresh(response);
                },
                error => {
                  console.error(
                    `❌ Failed to update non-phased event field:`,
                    error
                  );
                  // Revert local state on error
                  setDropdownValues(prev => ({
                    ...prev,
                    [id]: data[id] || ''
                  }));
                  if (onGeneralInfoChange) {
                    onGeneralInfoChange({ ...data, [id]: data[id] || '' });
                  }
                }
              );
            } else {
              console.warn('⚠️ Selected option not found for event:', newName);
            }
          } else if (id === 'parentMoment') {
            const options =
              mockDropdownOptions[
                dropdownFields.find(f => f.id === id)?.optionsKey || ''
              ];
            const selectedOption = options.find(opt => opt.name === newName);
            if (selectedOption) {
              const fieldData = {
                id: selectedOption.id,
                name: selectedOption.name
              };
              await updateParentMomentField(
                issueId,
                fieldData,
                response => {
                  if (onSeamlessRefresh) onSeamlessRefresh(response);
                  if (onNotificationRefresh) onNotificationRefresh(response);

                  if (onGeneralInfoChange) {
                    onGeneralInfoChange({
                      ...data,
                      [id]: fieldData,
                      _triggerMilestoneRefresh: true // Flag to trigger milestone refresh
                    });
                  }
                },
                error => {
                  console.error(
                    `❌ Failed to update non-phased parent moment field:`,
                    error
                  );
                  // Revert local state on error
                  setDropdownValues(prev => ({
                    ...prev,
                    [id]: data[id] || ''
                  }));
                  if (onGeneralInfoChange) {
                    onGeneralInfoChange({ ...data, [id]: data[id] || '' });
                  }
                }
              );
            }
          } else {
            // Use generic non-phased utility for other fields
            await updateNonPhasedField(
              issueId,
              id,
              newId,
              response => {
                // Trigger milestone refresh on tier change
                if (id === 'tier' && onMilestoneRefresh) {
                  onMilestoneRefresh(response);
                }
              },
              error => {
                console.error(
                  `❌ Failed to update non-phased field '${id}':`,
                  error
                );
                // Revert local state on error
                setDropdownValues(prev => ({ ...prev, [id]: data[id] || '' }));
                if (onGeneralInfoChange) {
                  onGeneralInfoChange({ ...data, [id]: data[id] || '' });
                }
              }
            );
          }
        }
      } catch (error) {
        console.error(`❌ Error updating field '${id}':`, error);
        // Revert local state on error
        setDropdownValues(prev => ({ ...prev, [id]: data[id] || '' }));
        if (onGeneralInfoChange) {
          onGeneralInfoChange({ ...data, [id]: data[id] || '' });
        }
      }
    }
  };

  // DateComponent change handler - handles format changes and nulls out old formats
  const handleDateChange = async (fieldName: string, value: any) => {
    // UI field ids (marketingMoment, releaseDate, effectiveDate) do not match API keys:
    // marketing uses targetStart | targetMonth | targetQuarter | targetDateStatus, etc.
    const dates = data?.dates;
    let oldDateValue: string | null | undefined;
    if (fieldName === 'marketingMoment') {
      if (!dates) {
        oldDateValue = undefined;
      } else {
        const dateValue =
          dates.targetStart || dates.targetMonth || dates.targetQuarter || null;
        oldDateValue =
          !dateValue && dates.targetDateStatus
            ? dates.targetDateStatus
            : dateValue;
      }
    } else if (fieldName === 'releaseDate') {
      if (!dates) {
        oldDateValue = undefined;
      } else {
        const dateValue =
          dates.releaseDate ||
          dates.releaseMonth ||
          dates.releaseQuarter ||
          null;
        oldDateValue =
          !dateValue && dates.releaseDateStatus
            ? dates.releaseDateStatus
            : dateValue;
      }
    } else if (fieldName === 'effectiveDate') {
      if (!dates) {
        oldDateValue = undefined;
      } else {
        const dateValue =
          dates.effectiveDate ||
          dates.effectiveMonth ||
          dates.effectiveQuarter ||
          null;
        oldDateValue =
          !dateValue && dates.effectiveDateStatus
            ? dates.effectiveDateStatus
            : dateValue;
      }
    } else if (fieldName === 'customerNotificationDate') {
      if (!dates) {
        oldDateValue = undefined;
      } else if (isSpecialDateStatus(dates.customerNotificationDateStatus)) {
        oldDateValue = dates.customerNotificationDateStatus;
      } else {
        const dateValue =
          dates.customerNotificationDate ||
          dates.customerNotificationMonth ||
          dates.customerNotificationQuarter ||
          null;
        oldDateValue =
          !dateValue && dates.customerNotificationDateStatus
            ? dates.customerNotificationDateStatus
            : dateValue;
      }
    } else {
      oldDateValue = dates?.[fieldName as keyof typeof dates] as
        | string
        | undefined;
    }
    const normalizedOldValue =
      oldDateValue === null || oldDateValue === undefined
        ? 'N/A'
        : String(oldDateValue);

    const updatedDateValues = { ...dateValues };

    if (fieldName === 'marketingMoment') {
      updatedDateValues.marketingMoment = value;
      setDateValues(updatedDateValues);

      // Determine new field and value based on type
      let newField = '';
      let dateStr = '';

      if (value.type === 'months' && value.months?.length > 0) {
        let monthValue = value.months[0];

        // If the calendar returned type: 'months', trust that it's a month selection
        // Don't try to auto-detect quarters - the calendar handles that distinction
        // This fixes the bug where selecting Dec, Mar, Jun, Sep was incorrectly treated as quarters
        newField = 'targetMonth';

        // Convert YYYY-MM format to "Mon YYYY" format if needed
        if (monthValue && monthValue.match(/^\d{4}-\d{2}$/)) {
          const year = monthValue.substring(0, 4);
          const monthNum = monthValue.substring(5, 7);
          const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
          ];
          const monthName = monthNames[parseInt(monthNum, 10) - 1];
          monthValue = `${monthName} ${year}`; // Format as "Oct 2025"
        }

        dateStr = monthValue;
      } else if (value.type === 'quarters' && value.quarters?.length > 0) {
        newField = 'targetQuarter';
        let quarterValue = value.quarters[0];

        // Convert any date format to proper "Q1 YYYY" format if needed
        // Quarter mapping: Q1=Dec-Feb, Q2=Mar-May, Q3=Jun-Aug, Q4=Sep-Nov
        if (quarterValue && quarterValue.match(/^\d{4}-\d{2}-01$/)) {
          const year = quarterValue.substring(0, 4);
          const monthNum = quarterValue.substring(5, 7);
          let quarter = '';
          switch (monthNum) {
            case '12':
              quarter = 'Q1';
              break; // December starts Q1
            case '01':
            case '02':
              quarter = 'Q1';
              break; // Jan-Feb continue Q1
            case '03':
            case '04':
            case '05':
              quarter = 'Q2';
              break; // Mar-May is Q2
            case '06':
            case '07':
            case '08':
              quarter = 'Q3';
              break; // Jun-Aug is Q3
            case '09':
            case '10':
            case '11':
              quarter = 'Q4';
              break; // Sep-Nov is Q4
          }

          // For December, the quarter year should be the following year
          const quarterYear =
            monthNum === '12' ? (parseInt(year) + 1).toString() : year;
          quarterValue = `${quarter} ${quarterYear}`; // Format as "Q1 2025"
        }

        dateStr = quarterValue;
      } else if (value.type === 'date' && value.date) {
        // ✅ FIX: Do NOT treat dates with day=01 as month selections
        // The calendar already returns type: 'months' when user selects a month
        // This block should only handle actual date selections (including Jan 1, Feb 1, etc.)
        const dateValue = value.date;
        if (typeof dateValue === 'string') {
          // Always treat type: 'date' as an actual date selection
          // Store as ISO date format (e.g., "2025-01-01")
          newField = 'targetStart';
          dateStr = dateValue;
        } else {
          newField = 'targetStart';
          dateStr = value.date;
        }
      } else if (value.type === 'special') {
        newField = 'targetDateStatus';
        dateStr = value.value;
      }

      // Check if old value was TBD/N/A - if so, clear targetDateStatus when changing to actual dates
      const oldTargetDateStatus = data?.dates?.targetDateStatus;
      const wasTbdOrNa =
        oldTargetDateStatus === 'TBD' || oldTargetDateStatus === 'N/A';
      const isChangingToActualDate =
        newField === 'targetMonth' ||
        newField === 'targetQuarter' ||
        newField === 'targetStart';

      // Create dates object with format change handling
      const newDatesObject = {
        ...data.dates,
        // Set the new field
        [newField]: dateStr,
        // Null out other target formats
        ...(newField !== 'targetMonth' && { targetMonth: null }),
        ...(newField !== 'targetQuarter' && { targetQuarter: null }),
        ...(newField !== 'targetStart' && { targetStart: null }),
        // Clear targetDateStatus when:
        // 1. Setting a non-special value (month/quarter/date), OR
        // 2. Changing from TBD/N/A to actual dates
        ...((newField !== 'targetDateStatus' ||
          (wasTbdOrNa && isChangingToActualDate)) && { targetDateStatus: null })
      };

      // Auto-disable showOnRoadmap if target date is set to N/A/TBD (for phased layouts)
      // Auto-disable showOnRoadmap/mirrorRelease if target date is set to N/A/TBD (for non-phased layouts)
      // This ensures the toggle state reflects the disabled state
      if (isPhased && (dateStr === 'N/A' || dateStr === 'TBD')) {
        // ✅ ONLY update if moment type supports showOnRoadmap toggle
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        // ✅ FIX: Get fresh value from data to avoid stale state issues
        // When user manually toggles, data gets updated but local state might be stale
        const currentShowOnRoadmapValue =
          (data as any)?.showOnRoadmap ?? showOnRoadmap;

        console.log(
          '🔍 Phased: Target date changed to TBD/N/A - checking showOnRoadmap',
          {
            dateStr,
            useShowOnRoadmap,
            localStateShowOnRoadmap: showOnRoadmap,
            dataShowOnRoadmap: (data as any)?.showOnRoadmap,
            currentShowOnRoadmapValue,
            willUpdate: useShowOnRoadmap && currentShowOnRoadmapValue === true
          }
        );

        if (useShowOnRoadmap && currentShowOnRoadmapValue === true) {
          setShowOnRoadmap(false);
        }
        // If moment type doesn't support showOnRoadmap, don't send patch
      } else if (!isPhased && (dateStr === 'N/A' || dateStr === 'TBD')) {
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);
        const currentShowOnRoadmapValue =
          (data as any)?.showOnRoadmap ?? showOnRoadmap;
        const currentMirrorReleaseValue =
          (data as any)?.mirrorRelease ?? mirrorRelease;

        if (useShowOnRoadmap && currentShowOnRoadmapValue === true) {
          setShowOnRoadmap(false);
        } else if (!useShowOnRoadmap && currentMirrorReleaseValue === true) {
          setMirrorRelease(false);
        }
      } else {
        const newTargetDateComparable = getComparableDate(value);
        const currentReleaseDateComparable = getComparableDate(
          dateValues.releaseDate
        );
        const currentEffectiveDateComparable = getComparableDate(
          dateValues.effectiveDate
        );
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        if (
          isPhased &&
          newTargetDateComparable &&
          currentReleaseDateComparable &&
          newTargetDateComparable === currentReleaseDateComparable &&
          useShowOnRoadmap &&
          showOnRoadmap !== false
        ) {
          setShowOnRoadmap(false);
        } else if (!isPhased) {
          const targetMatchesEffective =
            currentEffectiveDateComparable &&
            newTargetDateComparable === currentEffectiveDateComparable;
          const targetMatchesRelease =
            currentReleaseDateComparable &&
            newTargetDateComparable === currentReleaseDateComparable;

          if (
            useShowOnRoadmap &&
            (targetMatchesEffective || targetMatchesRelease) &&
            showOnRoadmap !== false
          ) {
            setShowOnRoadmap(false);
          } else if (
            !useShowOnRoadmap &&
            (targetMatchesEffective || targetMatchesRelease) &&
            mirrorRelease
          ) {
            setMirrorRelease(false);
          }
        }
      }

      // Update parent component state immediately for instant UI feedback
      // Only pass the dates that changed, not the entire data object (to avoid overwriting other fields like event)
      if (onGeneralInfoChange) {
        onGeneralInfoChange({
          dates: newDatesObject
        });
      }

      // Update via API with format change handling
      if (issueId && dateStr) {
        pendingDateFieldsRef.current.add('marketingMoment');
        try {
          let patchResponse: unknown;
          if (isPhased && data.id) {
            // Phased layout - use custom date format change handler
            patchResponse = await updatePhasedDateFormat(
              issueId,
              data.id,
              newDatesObject,
              phaseName
            );
          } else {
            // Non-phased layout - use custom date format change handler
            patchResponse = await updateNonPhasedDateFormat(
              issueId,
              newDatesObject
            );
          }

          await applyToggleSideEffectsForSavedDate(
            'marketingMoment',
            value,
            dateStr
          );

          // 3. ✅ Call notification API AFTER successful PATCH
          if (onNotificationRefresh) {
            onNotificationRefresh(patchResponse);
          }
        } catch (error) {
          console.error(`❌ Error updating marketing moment:`, error);
          // Revert the state on error - only pass dates to avoid overwriting other fields
          if (onGeneralInfoChange) {
            onGeneralInfoChange({
              dates: data.dates || {}
            });
          }
        } finally {
          pendingDateFieldsRef.current.delete('marketingMoment');
        }
      }
    } else if (fieldName === 'releaseDate') {
      updatedDateValues.releaseDate = value;
      setDateValues(updatedDateValues);

      // Determine new field and value based on type
      let newField = '';
      let dateStr = '';

      if (value.type === 'months' && value.months?.length > 0) {
        let monthValue = value.months[0];

        // If the calendar returned type: 'months', trust that it's a month selection
        // Don't try to auto-detect quarters - the calendar handles that distinction
        // This fixes the bug where selecting Dec, Mar, Jun, Sep was incorrectly treated as quarters
        newField = 'releaseMonth';

        // Convert YYYY-MM format to "Mon YYYY" format if needed
        if (monthValue && monthValue.match(/^\d{4}-\d{2}$/)) {
          const year = monthValue.substring(0, 4);
          const monthNum = monthValue.substring(5, 7);
          const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
          ];
          const monthName = monthNames[parseInt(monthNum, 10) - 1];
          monthValue = `${monthName} ${year}`; // Format as "Oct 2025"
        }

        dateStr = monthValue;
      } else if (value.type === 'quarters' && value.quarters?.length > 0) {
        newField = 'releaseQuarter';
        let quarterValue = value.quarters[0];

        // Convert any date format to proper "Q1 YYYY" format if needed
        // Quarter mapping: Q1=Dec-Feb, Q2=Mar-May, Q3=Jun-Aug, Q4=Sep-Nov
        if (quarterValue && quarterValue.match(/^\d{4}-\d{2}-01$/)) {
          const year = quarterValue.substring(0, 4);
          const monthNum = quarterValue.substring(5, 7);
          let quarter = '';
          switch (monthNum) {
            case '12':
              quarter = 'Q1';
              break; // December starts Q1
            case '01':
            case '02':
              quarter = 'Q1';
              break; // Jan-Feb continue Q1
            case '03':
            case '04':
            case '05':
              quarter = 'Q2';
              break; // Mar-May is Q2
            case '06':
            case '07':
            case '08':
              quarter = 'Q3';
              break; // Jun-Aug is Q3
            case '09':
            case '10':
            case '11':
              quarter = 'Q4';
              break; // Sep-Nov is Q4
          }

          // For December, the quarter year should be the following year
          const quarterYear =
            monthNum === '12' ? (parseInt(year) + 1).toString() : year;
          quarterValue = `${quarter} ${quarterYear}`; // Format as "Q1 2025"
        }

        dateStr = quarterValue;
      } else if (value.type === 'date' && value.date) {
        // ✅ FIX: Do NOT treat dates with day=01 as month selections
        // The calendar already returns type: 'months' when user selects a month
        const dateValue = value.date;
        if (typeof dateValue === 'string') {
          // Always treat type: 'date' as an actual date selection
          newField = 'releaseDate';
          dateStr = dateValue;
        } else {
          newField = 'releaseDate';
          dateStr = value.date;
        }
      } else if (value.type === 'special') {
        newField = 'releaseDateStatus';
        dateStr = value.value;
      }

      // Check if old value was TBD/N/A - if so, clear releaseDateStatus when changing to actual dates
      const oldReleaseDateStatus = data?.dates?.releaseDateStatus;
      const wasReleaseTbdOrNa =
        oldReleaseDateStatus === 'TBD' || oldReleaseDateStatus === 'N/A';
      const isChangingToActualReleaseDate =
        newField === 'releaseMonth' ||
        newField === 'releaseQuarter' ||
        newField === 'releaseDate';

      // Create dates object with format change handling
      const newDatesObject = {
        ...data.dates,
        // Set the new field
        [newField]: dateStr,
        // Null out other release formats
        ...(newField !== 'releaseMonth' && { releaseMonth: null }),
        ...(newField !== 'releaseQuarter' && { releaseQuarter: null }),
        ...(newField !== 'releaseDate' && { releaseDate: null }),
        // Clear releaseDateStatus when changing to actual dates
        ...((newField !== 'releaseDateStatus' ||
          (wasReleaseTbdOrNa && isChangingToActualReleaseDate)) && {
          releaseDateStatus: null
        })
      };

      // Auto-disable showOnRoadmap if release date is set to N/A/TBD (for phased layouts)
      // Auto-disable showOnRoadmap/mirrorRelease if release date is set to N/A/TBD (for non-phased layouts)
      // This ensures the toggle state reflects the disabled state
      if (isPhased && (dateStr === 'N/A' || dateStr === 'TBD')) {
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);
        if (useShowOnRoadmap && showOnRoadmap !== false) {
          setShowOnRoadmap(false);
        }
      } else if (!isPhased && (dateStr === 'N/A' || dateStr === 'TBD')) {
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);
        if (useShowOnRoadmap && showOnRoadmap !== false) {
          setShowOnRoadmap(false);
        } else if (!useShowOnRoadmap && mirrorRelease !== false) {
          setMirrorRelease(false);
        }
      } else {
        const newReleaseDateComparable = getComparableDate(value);
        const currentTargetDateComparable = getComparableDate(
          dateValues.marketingMoment
        );
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        if (
          isPhased &&
          newReleaseDateComparable &&
          currentTargetDateComparable &&
          newReleaseDateComparable === currentTargetDateComparable &&
          useShowOnRoadmap &&
          showOnRoadmap !== false
        ) {
          setShowOnRoadmap(false);
        } else if (!isPhased) {
          const currentEffectiveDateComparable = getComparableDate(
            dateValues.effectiveDate
          );
          const releaseMatchesEffective =
            currentEffectiveDateComparable &&
            newReleaseDateComparable === currentEffectiveDateComparable;
          const releaseMatchesTarget =
            currentTargetDateComparable &&
            newReleaseDateComparable === currentTargetDateComparable;

          if (
            useShowOnRoadmap &&
            (releaseMatchesTarget || releaseMatchesEffective) &&
            showOnRoadmap
          ) {
            setShowOnRoadmap(false);
          } else if (
            !useShowOnRoadmap &&
            (releaseMatchesEffective || releaseMatchesTarget) &&
            mirrorRelease
          ) {
            setMirrorRelease(false);
          }
        }
      }

      // Update parent component state immediately for instant UI feedback
      // Only pass the dates that changed, not the entire data object (to avoid overwriting other fields like event)
      if (onGeneralInfoChange) {
        onGeneralInfoChange({
          dates: newDatesObject
        });
      }

      // Update via API with format change handling
      if (issueId && dateStr) {
        pendingDateFieldsRef.current.add('releaseDate');
        try {
          let patchResponse: unknown;
          if (isPhased && data.id) {
            // Phased layout - use custom date format change handler
            patchResponse = await updatePhasedDateFormat(
              issueId,
              data.id,
              newDatesObject,
              phaseName
            );
          } else {
            // Non-phased layout - use custom date format change handler
            patchResponse = await updateNonPhasedDateFormat(
              issueId,
              newDatesObject
            );
          }

          await applyToggleSideEffectsForSavedDate(
            'releaseDate',
            value,
            dateStr
          );

          // 3. ✅ Call notification API AFTER successful PATCH
          if (onNotificationRefresh) {
            onNotificationRefresh(patchResponse);
          }
        } catch (error) {
          console.error(`❌ Error updating release date:`, error);
          // Revert the state on error - only pass dates to avoid overwriting other fields
          if (onGeneralInfoChange) {
            onGeneralInfoChange({
              dates: data.dates || {}
            });
          }
        } finally {
          pendingDateFieldsRef.current.delete('releaseDate');
        }
      }
    } else if (fieldName === 'effectiveDate') {
      updatedDateValues.effectiveDate = value;
      setDateValues(updatedDateValues);

      // Determine new field and value based on type (same logic as releaseDate)
      let newField = 'effectiveDate';
      let dateStr = '';

      if (value.type === 'months' && value.months?.length > 0) {
        let monthValue = value.months[0];

        // If the calendar returned type: 'months', trust that it's a month selection
        // Don't try to auto-detect quarters - the calendar handles that distinction
        // This fixes the bug where selecting Dec, Mar, Jun, Sep was incorrectly treated as quarters
        newField = 'effectiveMonth';

        // Convert YYYY-MM format to "Mon YYYY" format if needed
        if (monthValue && monthValue.match(/^\d{4}-\d{2}$/)) {
          const year = monthValue.substring(0, 4);
          const monthNum = monthValue.substring(5, 7);
          const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
          ];
          const monthName = monthNames[parseInt(monthNum, 10) - 1];
          monthValue = `${monthName} ${year}`; // Format as "Oct 2025"
        }

        dateStr = monthValue;
      } else if (value.type === 'quarters' && value.quarters?.length > 0) {
        newField = 'effectiveQuarter';
        let quarterValue = value.quarters[0];

        // Convert any date format to proper "Q1 YYYY" format if needed
        // Quarter mapping: Q1=Dec-Feb, Q2=Mar-May, Q3=Jun-Aug, Q4=Sep-Nov
        if (quarterValue && quarterValue.match(/^\d{4}-\d{2}-01$/)) {
          const year = quarterValue.substring(0, 4);
          const monthNum = quarterValue.substring(5, 7);
          let quarter = '';
          switch (monthNum) {
            case '12':
              quarter = 'Q1';
              break; // December starts Q1
            case '01':
            case '02':
              quarter = 'Q1';
              break; // Jan-Feb continue Q1
            case '03':
            case '04':
            case '05':
              quarter = 'Q2';
              break; // Mar-May is Q2
            case '06':
            case '07':
            case '08':
              quarter = 'Q3';
              break; // Jun-Aug is Q3
            case '09':
            case '10':
            case '11':
              quarter = 'Q4';
              break; // Sep-Nov is Q4
          }

          // For December, the quarter year should be the following year
          const quarterYear =
            monthNum === '12' ? (parseInt(year) + 1).toString() : year;
          quarterValue = `${quarter} ${quarterYear}`; // Format as "Q1 2025"
        }

        dateStr = quarterValue;
      } else if (value.type === 'date' && value.date) {
        // ✅ FIX: Do NOT treat dates with day=01 as month selections
        // The calendar already returns type: 'months' when user selects a month
        const dateValue = value.date;
        if (typeof dateValue === 'string') {
          // Always treat type: 'date' as an actual date selection
          newField = 'effectiveDate';
          dateStr = dateValue;
        } else {
          newField = 'effectiveDate';
          dateStr = value.date;
        }
      } else if (value.type === 'special') {
        newField = 'effectiveDateStatus';
        dateStr = value.value;
      }

      // Check if old value was TBD/N/A - if so, clear effectiveDateStatus when changing to actual dates
      const oldEffectiveDateStatus = data?.dates?.effectiveDateStatus;
      const wasEffectiveTbdOrNa =
        oldEffectiveDateStatus === 'TBD' || oldEffectiveDateStatus === 'N/A';
      const isChangingToActualEffectiveDate =
        newField === 'effectiveMonth' ||
        newField === 'effectiveQuarter' ||
        newField === 'effectiveDate';

      // Create dates object with format change handling (same as releaseDate)
      const newDatesObject = {
        ...data.dates,
        // Set the new field
        [newField]: dateStr,
        // Null out other effective formats when switching
        ...(newField !== 'effectiveMonth' && { effectiveMonth: null }),
        ...(newField !== 'effectiveQuarter' && { effectiveQuarter: null }),
        ...(newField !== 'effectiveDate' && { effectiveDate: null }),
        // Clear effectiveDateStatus when changing to actual dates
        ...((newField !== 'effectiveDateStatus' ||
          (wasEffectiveTbdOrNa && isChangingToActualEffectiveDate)) && {
          effectiveDateStatus: null
        })
      };

      // Auto-disable showOnRoadmap/mirrorRelease if effective date is set to N/A/TBD (for non-phased layouts)
      // This ensures the toggle state reflects the disabled state
      if (isPhased && (dateStr === 'N/A' || dateStr === 'TBD')) {
        // ✅ ONLY update if moment type supports showOnRoadmap toggle
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        if (useShowOnRoadmap) {
          console.log(
            '✅ Phased: Effective date set to TBD/N/A, setting showOnRoadmap to false'
          );
          // Always ensure showOnRoadmap is false when date is TBD/N/A
          if (showOnRoadmap !== false) {
            setShowOnRoadmap(false);
            handleShowOnRoadmapChange(false, {
              suppressSeamlessRefresh: true
            });
          } else {
            if (onRefreshNotifications) onRefreshNotifications();
          }
        }
        // If moment type doesn't support showOnRoadmap, don't send patch
      } else if (!isPhased && (dateStr === 'N/A' || dateStr === 'TBD')) {
        // For non-phased layouts, check which toggle to disable based on moment type
        const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

        console.log('✅ Non-phased: Effective date set to TBD/N/A', {
          useShowOnRoadmap,
          showOnRoadmap,
          mirrorRelease
        });

        if (useShowOnRoadmap) {
          // Always ensure showOnRoadmap is false
          if (showOnRoadmap !== false) {
            setShowOnRoadmap(false);
            handleShowOnRoadmapChange(false, {
              suppressSeamlessRefresh: true
            });
          } else {
            if (onRefreshNotifications) onRefreshNotifications();
          }
        } else if (!useShowOnRoadmap) {
          // Always ensure mirrorRelease is false
          if (mirrorRelease !== false) {
            setMirrorRelease(false);
            handleMirrorReleaseChange(false);
          } else {
            if (onRefreshNotifications) onRefreshNotifications();
          }
        }
      } else {
        // Check if dates are equal immediately (for instant feedback)
        // Compare the new effective date with target and release dates
        const newEffectiveDateComparable = getComparableDate(value);
        const currentTargetDateComparable = getComparableDate(
          dateValues.marketingMoment
        );
        const currentReleaseDateComparable = getComparableDate(
          dateValues.releaseDate
        );

        if (isPhased && newEffectiveDateComparable) {
          // For phased: check if effective equals either target or release
          // ✅ ONLY update if moment type supports showOnRoadmap toggle
          const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

          // PCO / EOL: compare effectiveDate vs customerNotificationDate
          if (usesPcoStylePhasedDates(momentType)) {
            const currentCustCommComparable = getComparableDate(
              dateValues.customerNotificationDate
            );
            if (
              useShowOnRoadmap &&
              currentCustCommComparable &&
              newEffectiveDateComparable === currentCustCommComparable
            ) {
              // Keep optimistic visuals; the date PATCH owns auto-hide and its audit.
              setShowOnRoadmap(false);
            }
          } else if (
            (currentTargetDateComparable &&
              newEffectiveDateComparable === currentTargetDateComparable) ||
            (currentReleaseDateComparable &&
              newEffectiveDateComparable === currentReleaseDateComparable)
          ) {
            if (useShowOnRoadmap) {
              console.log(
                '✅ Phased: Effective matches Target/Release, setting showOnRoadmap to false'
              );
              // Always ensure showOnRoadmap is false when dates match
              if (showOnRoadmap !== false) {
                setShowOnRoadmap(false);
                handleShowOnRoadmapChange(false, {
                  suppressSeamlessRefresh: true
                });
              } else {
                // Even if already false, ensure backend is in sync
                handleShowOnRoadmapChange(false, {
                  suppressSeamlessRefresh: true
                });
              }
            }
            // If moment type doesn't support showOnRoadmap, don't send patch
          }
        } else if (!isPhased && newEffectiveDateComparable) {
          // For non-phased: check if ANY two dates become equal
          // Check if new effective matches target date
          const effectiveMatchesTarget =
            currentTargetDateComparable &&
            newEffectiveDateComparable === currentTargetDateComparable;

          // Check if new effective matches release date
          const effectiveMatchesRelease =
            currentReleaseDateComparable &&
            newEffectiveDateComparable === currentReleaseDateComparable;

          // Determine which toggle to disable based on moment type
          const useShowOnRoadmap = shouldShowRoadmapToggle(momentType);

          console.log('🔍 Effective date change - Equal dates check:', {
            momentType,
            useShowOnRoadmap,
            effectiveMatchesTarget,
            effectiveMatchesRelease,
            showOnRoadmap,
            mirrorRelease,
            newEffectiveDateComparable,
            currentTargetDateComparable,
            currentReleaseDateComparable
          });

          if (useShowOnRoadmap) {
            // For moment types that use showOnRoadmap toggle (e.g., "Offer, Price Change, or Program")
            // Check if effective matches target date and set showOnRoadmap to false
            if (effectiveMatchesTarget || effectiveMatchesRelease) {
              console.log('✅ Dates match! Setting showOnRoadmap to false');
              // Always ensure showOnRoadmap is false when dates match, and sync with backend
              if (showOnRoadmap !== false) {
                setShowOnRoadmap(false);
                handleShowOnRoadmapChange(false, {
                  suppressSeamlessRefresh: true
                });
              } else {
                console.log(
                  '⚠️ showOnRoadmap already false, ensuring backend is in sync'
                );
                // Even if already false, ensure backend is in sync
                handleShowOnRoadmapChange(false, {
                  suppressSeamlessRefresh: true
                });
              }
            }
          } else {
            // For moment types that use mirrorRelease toggle
            // Check if effective matches target or release date and set mirrorRelease to false
            if (
              (effectiveMatchesTarget || effectiveMatchesRelease) &&
              mirrorRelease
            ) {
              setMirrorRelease(false);
              handleMirrorReleaseChange(false);
            }
          }
        }
      }

      // Update parent component state immediately for instant UI feedback
      // Only pass the dates that changed, not the entire data object (to avoid overwriting other fields like event)
      if (onGeneralInfoChange) {
        onGeneralInfoChange({
          dates: newDatesObject
        });
      }

      // Update via API with format change handling
      if (issueId && dateStr) {
        try {
          let patchResponse: unknown;
          if (isPhased && data.id) {
            const pcoDateFields = usesPcoStylePhasedDates(momentType)
              ? buildPCOCardDateField(newField, dateStr)
              : null;

            if (pcoDateFields) {
              // PCO (pco: true): card PATCH with typed date objects
              patchResponse = await updateMomentCard(issueId, {
                momentType,
                releasePhaseId: data.id,
                fields: { dates: pcoDateFields }
              });
            } else {
              // All other phased moment types (and PCO TBD/N/A status)
              patchResponse = await updatePhasedDateFormat(
                issueId,
                data.id,
                newDatesObject,
                phaseName
              );
            }
          } else {
            // Non-phased layout - use custom date format change handler
            patchResponse = await updateNonPhasedDateFormat(
              issueId,
              newDatesObject
            );
          }

          // 3. ✅ Call notification API AFTER successful PATCH
          if (onNotificationRefresh) {
            onNotificationRefresh(patchResponse);
          }
        } catch (error) {
          showUpdateErrorToast(
            'Failed to update date. Please try again.',
            error
          );
          // Revert the state on error - only pass dates to avoid overwriting other fields
          if (onGeneralInfoChange) {
            onGeneralInfoChange({
              dates: data.dates || {}
            });
          }
        }
      }
    } else if (fieldName === 'customerNotificationDate') {
      updatedDateValues.customerNotificationDate = value;
      setDateValues(updatedDateValues);

      let newField = 'customerNotificationDate';
      let dateStr = '';

      if (value.type === 'months' && value.months?.length > 0) {
        let monthValue = value.months[0];
        newField = 'customerNotificationMonth';
        if (monthValue && monthValue.match(/^\d{4}-\d{2}$/)) {
          const year = monthValue.substring(0, 4);
          const monthNum = monthValue.substring(5, 7);
          const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
          ];
          monthValue = `${monthNames[parseInt(monthNum, 10) - 1]} ${year}`;
        }
        dateStr = monthValue;
      } else if (value.type === 'quarters' && value.quarters?.length > 0) {
        newField = 'customerNotificationQuarter';
        dateStr = value.quarters[0];
      } else if (value.type === 'date') {
        newField = 'customerNotificationDate';
        dateStr = value.date;
      } else if (value.type === 'special') {
        newField = 'customerNotificationDateStatus';
        dateStr = value.value;
      }

      // Auto-disable showOnRoadmap when customerNotificationDate becomes N/A/TBD or
      // equals effectiveDate (toggle only makes sense when dates are different)
      if (shouldShowRoadmapToggle(momentType)) {
        if (dateStr === 'N/A' || dateStr === 'TBD') {
          if (showOnRoadmap !== false) {
            setShowOnRoadmap(false);
            handleShowOnRoadmapChange(false, {
              suppressSeamlessRefresh: true
            });
          }
        } else if (dateStr) {
          const newCustCommComparable = getComparableDate(value);
          const currentEffectiveComparable = getComparableDate(
            dateValues.effectiveDate
          );
          if (
            currentEffectiveComparable &&
            newCustCommComparable === currentEffectiveComparable
          ) {
            if (isPhased && usesPcoStylePhasedDates(momentType)) {
              // Avoid a racing user-toggle PATCH for a server-derived change.
              setShowOnRoadmap(false);
            } else if (showOnRoadmap !== false) {
              setShowOnRoadmap(false);
              handleShowOnRoadmapChange(false, {
                suppressSeamlessRefresh: true
              });
            } else {
              handleShowOnRoadmapChange(false, {
                suppressSeamlessRefresh: true
              });
            }
          }
        }
      }

      const oldCommStatus = data?.dates?.customerNotificationDateStatus;
      const wasCommTbdOrNa = oldCommStatus === 'TBD' || oldCommStatus === 'N/A';
      const isChangingToActualCommDate =
        newField === 'customerNotificationMonth' ||
        newField === 'customerNotificationQuarter' ||
        newField === 'customerNotificationDate';

      const newDatesObject = {
        ...data.dates,
        [newField]: dateStr,
        ...(newField !== 'customerNotificationMonth' && {
          customerNotificationMonth: null
        }),
        ...(newField !== 'customerNotificationQuarter' && {
          customerNotificationQuarter: null
        }),
        ...(newField !== 'customerNotificationDate' && {
          customerNotificationDate: null
        }),
        ...((newField !== 'customerNotificationDateStatus' ||
          (wasCommTbdOrNa && isChangingToActualCommDate)) && {
          customerNotificationDateStatus: null
        })
      };

      if (onGeneralInfoChange) {
        onGeneralInfoChange({ ...data, dates: newDatesObject });
      }

      if (issueId) {
        try {
          let patchResponse: unknown;
          if (isPhased && data.id) {
            const pcoDateFields = usesPcoStylePhasedDates(momentType)
              ? buildPCOCardDateField(newField, dateStr)
              : null;

            if (pcoDateFields) {
              // PCO (pco: true): card PATCH with typed date objects
              patchResponse = await updateMomentCard(issueId, {
                momentType,
                releasePhaseId: data.id,
                fields: { dates: pcoDateFields }
              });
            } else {
              // All other phased moment types (and PCO TBD/N/A status)
              patchResponse = await updatePhasedDateFormat(
                issueId,
                data.id,
                newDatesObject,
                phaseName
              );
            }
          } else {
            patchResponse = await updateNonPhasedDateFormat(
              issueId,
              newDatesObject
            );
          }

          // 3. ✅ Call notification API AFTER successful PATCH
          if (onNotificationRefresh) {
            onNotificationRefresh(patchResponse);
          }
        } catch (error) {
          showUpdateErrorToast(
            'Failed to update date. Please try again.',
            error
          );
          if (onGeneralInfoChange) {
            onGeneralInfoChange({ dates: data.dates || {} });
          }
        }
      }
    }
  };

  // Helper function for phased date format updates
  // Converts a flat date field name + value (from the calendar component) into the
  // typed date object required by the PCO /card PATCH endpoint.
  // Returns null for TBD/N/A status fields — those stay on the direct PATCH path.
  const buildPCOCardDateField = (
    flatField: string,
    value: string
  ): Record<string, any> | null => {
    if (flatField === 'effectiveDate')
      return { effectiveDate: { type: 'date', date: value } };
    if (flatField === 'effectiveMonth')
      return { effectiveDate: { type: 'month', months: [value] } };
    if (flatField === 'effectiveQuarter')
      return { effectiveDate: { type: 'quarter', quarters: [value] } };
    if (flatField === 'customerNotificationDate')
      return { customerNotificationDate: { type: 'date', date: value } };
    if (flatField === 'customerNotificationMonth')
      return { customerNotificationDate: { type: 'month', months: [value] } };
    if (flatField === 'customerNotificationQuarter')
      return {
        customerNotificationDate: { type: 'quarter', quarters: [value] }
      };
    return null; // status fields (TBD/N/A) — caller falls back to direct PATCH
  };

  const updatePhasedDateFormat = async (
    issueId: string,
    phaseId: string,
    datesObject: any,
    phaseName?: string
  ) => {
    const { updateMoment } = await import('../../RsdForms/api');

    const payload = {
      releasePhases: [
        {
          id: phaseId,
          ...(phaseName && { phaseName }),
          status: data.reportingStatus || 'ACTIVE',
          generalInfo: {
            dates: datesObject
          }
        }
      ]
    };

    return await updateMoment(issueId, payload);
  };

  // Helper function for non-phased date format updates
  const updateNonPhasedDateFormat = async (
    issueId: string,
    datesObject: any
  ) => {
    const { updateMoment } = await import('../../RsdForms/api');

    const payload = {
      generalInfo: {
        dates: datesObject
      }
    };

    return await updateMoment(issueId, payload);
  };

  // Close status dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusButtonRef.current &&
        !statusButtonRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }
    };

    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [statusDropdownOpen]);

  return (
    <div
      data-testid="general-info-container"
      className={styles.generalInfoContainer}
    >
      {isCollapsible && (
        <div
          data-testid="general-info-header"
          className={`${styles.generalInfoHeader} ${styles.collapsibleHeader}`}
          onClick={onToggle}
          style={{ cursor: 'pointer' }}
        >
          <div
            data-testid="general-info-chevron"
            className={styles.chevronContainer}
          >
            <ChevronIcon isExpanded={isExpanded} />
          </div>
          <Text
            data-testid="general-info-title"
            UNSAFE_className={styles.title}
          >
            General Info
          </Text>
          <button
            data-testid="general-info-edit-button"
            className={styles.editButton}
          >
            <Comment />
            <span
              data-testid="general-info-edit-badge"
              className={styles.editBadge}
            >
              1
            </span>
          </button>
        </div>
      )}
      {showContent && (!isCollapsible || isExpanded) && (
        <div
          data-testid="general-info-items-container"
          className={`${styles.infoItemsContainer} ${
            !isPhased ? styles.nonPhasedLayout : ''
          }`}
        >
          {/* Render dropdown fields */}
          {dropdownFields.map(field => {
            const options = mockDropdownOptions[field.optionsKey] || [];
            let value = getOptionNameById(options, data[field.id]);

            // Show "NULL" for empty tier in non-phased layouts
            if (field.id === 'tier' && (!value || value === '')) {
              value = 'NULL';
            }

            return (
              <div
                className={styles.infoItem}
                key={field.id}
                data-testid={`general-info-dropdown-field-${field.id}`}
                style={{ position: 'relative' }}
              >
                <EditableField
                  data-testid={`general-info-dropdown-editable-field-${field.id}`}
                  label={field.label}
                  value={value}
                  options={options.map(opt => opt.name)}
                  onChange={newValue =>
                    handleDropdownChange(field.id, newValue)
                  }
                  id={field.id}
                  type={getEditableFieldType(field.id)}
                  isPhased={isPhased}
                  disabled={!canEditGeneralInfo}
                />
              </div>
            );
          })}

          {/* Render inline calendars for date fields using RSD Calendar */}
          {dateFields.map(field => (
            <SimpleDateField
              key={field.id}
              data-testid={`general-info-date-field-${field.id}`}
              highlightIssue={(() => {
                const issue = getGeneralInfoDateHighlightIssue(field.id);
                if (!issueId || !issue) return null;
                return {
                  issueId,
                  fieldRef: issue.fieldRef,
                  ruleId: issue.ruleId
                };
              })()}
              field={{ label: field.label }}
              value={
                dateValues[field.id]
                  ? dateValues[field.id].type === 'date'
                    ? {
                        start: (dateValues[field.id] as any).date,
                        end: (dateValues[field.id] as any).date
                      }
                    : dateValues[field.id] // Pass months/quarters/special format directly
                  : null
              }
              isSingleDate={true}
              onChange={val => {
                // Handle different value types: date, months, quarters
                if (val && (val as any).type) {
                  if (field.id === 'marketingMoment') {
                    handleDateChange('marketingMoment', val as any);
                  } else if (field.id === 'releaseDate') {
                    handleDateChange('releaseDate', val as any);
                  } else if (field.id === 'effectiveDate') {
                    handleDateChange('effectiveDate', val as any);
                  } else if (field.id === 'customerNotificationDate') {
                    handleDateChange('customerNotificationDate', val as any);
                  }
                }
              }}
              // Toggle for release date (Show on Roadmap) and effective date (Mirror Release)
              showToggle={
                (field.id === 'releaseDate' && shouldShowToggleFields()) ||
                (field.id === 'effectiveDate' && shouldEnableMirrorRelease())
              }
              toggleChecked={
                field.id === 'releaseDate'
                  ? showOnRoadmap
                  : field.id === 'effectiveDate'
                  ? showOnRoadmap
                  : false
              }
              toggleDisabled={isToggleDisabled}
              onToggleChange={
                field.id === 'releaseDate'
                  ? handleShowOnRoadmapChange
                  : field.id === 'effectiveDate'
                  ? handleShowOnRoadmapChange
                  : undefined
              }
              toggleLabel={
                field.id === 'effectiveDate'
                  ? 'Show Additional Card'
                  : undefined
              }
              // Enable TBD for effectiveDate, releaseDate, marketingMoment, and customerNotificationDate
              disableTBD={
                field.id !== 'marketingMoment' &&
                field.id !== 'releaseDate' &&
                field.id !== 'effectiveDate' &&
                field.id !== 'customerNotificationDate'
              }
              // Disable N/A for marketingMoment and customerNotificationDate
              disableNA={
                field.id === 'marketingMoment' ||
                field.id === 'customerNotificationDate'
              }
              disabled={!canEditGeneralInfo}
              // Secondary date status — still passed because the overdue colouring reads it.
              // The Active/Completed control itself now lives in the phase status menu, so
              // there is no onSecondaryStatusChange here. Keyed on the moment type having a
              // secondary date, never on the additional-card toggle's checked state.
              secondaryDateStatus={
                isSecondaryDateField(field.id) ? secondaryDateStatus : undefined
              }
              // Status props for overdue determination
              primaryStatus={data.reportingStatus}
              fieldId={field.id}
            />
          ))}

          {/* Render event field separately with API data */}
          {specialFields.map(field => {
            // Use effective data (handles loading, empty, and error states)
            const options =
              field.optionsKey === 'events'
                ? effectiveEventsData
                : field.optionsKey === 'parentMoments'
                ? effectiveParentMomentsData
                : mockDropdownOptions[field.optionsKey] || [];
            // Safely extract value for EditableField
            let value = '';
            const fieldData = data[field.id];

            if (field.id === 'event' || field.id === 'parentMoment') {
              // For event and parentMoment fields that use ID-based comparison,
              // pass the ID as the value (since EditableField will compare option.id === value)
              if (typeof fieldData === 'string') {
                value = fieldData; // Assume it's already an ID
              } else if (fieldData && typeof fieldData === 'object') {
                value = fieldData.id || '';
              } else {
                // Fallback - try to find the ID from the name
                const foundOption = options.find(opt => opt.name === fieldData);
                value = foundOption ? foundOption.id : fieldData || '';
              }
            } else {
              // For other fields, use display name (existing behavior)
              if (typeof fieldData === 'string') {
                value = fieldData;
              } else if (fieldData && typeof fieldData === 'object') {
                value = fieldData.name || '';
              } else {
                value = getOptionNameById(options, fieldData);
              }
            }

            // Ensure value is always a string
            value = typeof value === 'string' ? value : '';

            // Show "NULL" for empty values (display purposes)
            if (field.id === 'parentMoment' && (!value || value === '')) {
              value = 'NULL';
            }

            if (field.id === 'event' && (!value || value === '')) {
              value = 'NULL';
            }

            // Show loading state for events
            const isLoadingThisField =
              field.optionsKey === 'events' && isLoadingEvents;

            // For event and parentMoment fields, pass option objects to enable ID-based comparison
            // For other fields, keep existing behavior (string names only)
            const fieldOptions =
              field.id === 'event' || field.id === 'parentMoment'
                ? options // Pass full objects with id and name
                : options.map(opt => opt.name); // Legacy behavior for other fields

            return (
              <div
                className={styles.infoItem}
                key={field.id}
                data-field-id={field.id}
                data-testid={`general-info-field-${field.id}`}
                style={{ position: 'relative' }}
              >
                <EditableField
                  data-testid={`general-info-editable-field-${field.id}`}
                  label={field.label}
                  value={isLoadingThisField ? 'Loading...' : value}
                  options={fieldOptions}
                  onChange={async newValue => {
                    // Prevent interaction with loading or placeholder options
                    if (
                      isLoadingThisField ||
                      newValue === 'Loading events...' ||
                      newValue === 'No events available' ||
                      newValue === 'Loading parent moments...' ||
                      newValue === 'No parent moments available'
                    ) {
                      return;
                    }

                    const selectedOption = options.find(
                      opt => opt.name === newValue
                    );

                    if (selectedOption) {
                      const fieldData = {
                        id: selectedOption.id,
                        name: selectedOption.name
                      };

                      // Update parent component state immediately for instant UI feedback
                      if (onGeneralInfoChange) {
                        const updatedData = {
                          ...data,
                          [field.id]: fieldData
                        };
                        onGeneralInfoChange(updatedData);
                      }

                      // Update via API using the utility
                      if (issueId) {
                        if (field.id === 'event') {
                          // For events, use the appropriate handler based on layout type
                          if (isPhased && data.id) {
                            // Use phased event update for phased layouts
                            await updatePhasedEventField(
                              issueId,
                              data.id, // phaseId
                              fieldData,
                              phaseName,
                              data.reportingStatus || 'ACTIVE', // status
                              response => {
                                if (onSeamlessRefresh)
                                  onSeamlessRefresh(response);
                                if (onNotificationRefresh)
                                  onNotificationRefresh(response);
                              },
                              error => {
                                showUpdateErrorToast(
                                  'Failed to update event. Please try again.',
                                  error
                                );
                                // Revert state on error
                                if (onGeneralInfoChange) {
                                  onGeneralInfoChange({
                                    ...data,
                                    [field.id]: data[field.id] || null
                                  });
                                }
                              }
                            );
                          } else {
                            // Use non-phased event update for non-phased layouts
                            await updateEventField(
                              issueId,
                              fieldData,
                              response => {
                                if (onSeamlessRefresh)
                                  onSeamlessRefresh(response);
                                if (onNotificationRefresh)
                                  onNotificationRefresh(response);
                              },
                              error => {
                                showUpdateErrorToast(
                                  'Failed to update event. Please try again.',
                                  error
                                );
                                // Revert state on error
                                if (onGeneralInfoChange) {
                                  onGeneralInfoChange({
                                    ...data,
                                    [field.id]: data[field.id] || null
                                  });
                                }
                              }
                            );
                          }
                        } else if (field.id === 'parentMoment') {
                          // For parent moment, use the appropriate handler based on layout type
                          if (isPhased && data.id) {
                            // Use phased parent moment update for phased layouts
                            await updatePhasedParentMomentField(
                              issueId,
                              data.id, // phaseId
                              fieldData,
                              phaseName,
                              data.reportingStatus || 'ACTIVE', // status
                              response => {
                                if (onSeamlessRefresh)
                                  onSeamlessRefresh(response);
                                if (onNotificationRefresh)
                                  onNotificationRefresh(response);

                                // Trigger milestone refresh for inherited milestones
                                if (onGeneralInfoChange) {
                                  onGeneralInfoChange({
                                    ...data,
                                    [field.id]: fieldData,
                                    _triggerMilestoneRefresh: true // Flag to trigger milestone refresh
                                  });
                                }
                              },
                              error => {
                                showUpdateErrorToast(
                                  'Failed to update parent moment. Please try again.',
                                  error
                                );
                                // Revert state on error
                                if (onGeneralInfoChange) {
                                  onGeneralInfoChange({
                                    ...data,
                                    [field.id]: data[field.id] || null
                                  });
                                }
                              }
                            );
                          } else {
                            // Use non-phased parent moment update for non-phased layouts
                            await updateParentMomentField(
                              issueId,
                              fieldData,
                              response => {
                                if (onSeamlessRefresh)
                                  onSeamlessRefresh(response);
                                if (onNotificationRefresh)
                                  onNotificationRefresh(response);

                                // Trigger milestone refresh for inherited milestones
                                if (onGeneralInfoChange) {
                                  onGeneralInfoChange({
                                    ...data,
                                    [field.id]: fieldData,
                                    _triggerMilestoneRefresh: true // Flag to trigger milestone refresh
                                  });
                                }
                              },
                              error => {
                                showUpdateErrorToast(
                                  'Failed to update parent moment. Please try again.',
                                  error
                                );
                                // Revert state on error
                                if (onGeneralInfoChange) {
                                  onGeneralInfoChange({
                                    ...data,
                                    [field.id]: data[field.id] || null
                                  });
                                }
                              }
                            );
                          }
                        }
                      }
                    }
                  }}
                  id={field.id}
                  type={getEditableFieldType(field.id)}
                  isPhased={isPhased}
                  disabled={!canEditGeneralInfo}
                />
                {/* Show error message for events if API fails */}
                {field.optionsKey === 'events' &&
                  eventsError &&
                  !isLoadingEvents && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '1px',
                        right: '0',
                        fontSize: '8px',
                        color: '#d13212',
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '1px 3px',
                        borderRadius: '2px',
                        border: '1px solid #d13212',
                        zIndex: 1000,
                        whiteSpace: 'nowrap',
                        cursor: 'help'
                      }}
                      title="Authentication required to access events (using cached data)"
                    >
                      ⚠️
                    </div>
                  )}

                {/* Show error message for parent moments if API fails or no data available */}
                {field.optionsKey === 'parentMoments' &&
                  !isLoadingParentMoments &&
                  (parentMomentsError ||
                    (effectiveParentMomentsData.length === 1 &&
                      effectiveParentMomentsData[0].id === 'none')) && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '3px',
                        right: '0',
                        fontSize: '8px',
                        color: '#d13212',
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '1px 3px',
                        borderRadius: '2px',
                        border: '1px solid #d13212',
                        zIndex: 1000,
                        whiteSpace: 'nowrap',
                        cursor: 'help'
                      }}
                      title={
                        parentMomentsError
                          ? 'Authentication required to access parent moments (using cached data)'
                          : 'No parent moments found from API, showing fallback options'
                      }
                    >
                      ⚠️
                    </div>
                  )}

                {/* Show info message for empty events list */}
                {field.optionsKey === 'events' &&
                  !isLoadingEvents &&
                  !eventsError &&
                  effectiveEventsData.length === 1 &&
                  effectiveEventsData[0].id === 'none' && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6B6B6B',
                        marginTop: '4px',
                        maxWidth: '100%',
                        wordWrap: 'break-word',
                        lineHeight: '1.3'
                      }}
                    >
                      ℹ️ No events found from API, showing fallback options
                    </div>
                  )}

                {/* Inherit Milestones Toggle - Show for phased releases or when parent moment exists */}
                {field.id === 'parentMoment' && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {shouldShowInheritMilestonesToggle() && (
                      <>
                        <Switch
                          checked={inheritMilestones}
                          disabled={
                            // ✅ FIX: Removed one-way toggle restriction
                            // Only disable if no parent moment is selected
                            !data.parentMoment ||
                            data.parentMoment === 'none' ||
                            (typeof data.parentMoment === 'object' &&
                              (!data.parentMoment?.name ||
                                data.parentMoment?.name === 'N/A' ||
                                data.parentMoment?.id === 'N/A')) ||
                            (typeof data.parentMoment === 'string' &&
                              (data.parentMoment === 'none' ||
                                data.parentMoment === '' ||
                                data.parentMoment === 'N/A' ||
                                data.parentMoment === 'NULL'))
                          }
                          onChange={handleInheritMilestonesChange}
                          label="Inherit Milestones"
                          toolTipMessage={INHERIT_MILESTONE_TOOLTIP_MESSAGE}
                        />
                        {/* Error badge inline with toggle */}
                        {parentMomentsError && !isLoadingParentMoments && (
                          <div
                            style={{
                              fontSize: '8px',
                              color: '#d13212',
                              background: 'rgba(255, 255, 255, 0.9)',
                              padding: '1px 3px',
                              borderRadius: '2px',
                              border: '1px solid #d13212',
                              whiteSpace: 'nowrap',
                              cursor: 'help'
                            }}
                            title="Authentication required to access parent moments (using cached data)"
                          >
                            ⚠️
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Calendar Portal */}
      {activeCalendar &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 100000 // Below header (100001) but above content
              }}
              onClick={() => setActiveCalendar(null)}
            />
            {/* Calendar */}
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 100000 // Below header (100001) but above content
              }}
              onClick={e => e.stopPropagation()}
            >
              <CalendarRange
                defaultDate={getCurrentDateValueForField(activeCalendar)}
                onCalendarDateChange={(
                  date,
                  isQuarterSelection,
                  selectedQuarter,
                  isMonthSelection,
                  selectedMonth,
                  specialValue
                ) => {
                  // If specialValue is null (TBD unchecked), don't close calendar
                  if (specialValue === null) {
                    handleCalendarSelectionForField(
                      activeCalendar,
                      date,
                      isQuarterSelection,
                      selectedQuarter,
                      isMonthSelection,
                      selectedMonth,
                      specialValue
                    );
                    // Keep calendar open - don't call setActiveCalendar(null)
                    return;
                  }

                  // For all other selections, handle and close calendar
                  handleCalendarSelectionForField(
                    activeCalendar,
                    date,
                    isQuarterSelection,
                    selectedQuarter,
                    isMonthSelection,
                    selectedMonth,
                    specialValue
                  );
                  setActiveCalendar(null);
                }}
                mode="single"
                skipTriggerButton={true}
                hideYearList={true}
                showTBDNA={true}
                defaultSpecialValue={getCurrentSpecialValueForField(
                  activeCalendar
                )}
                disablePastDate={false}
                disableNA={true}
                disableTBD={activeCalendar !== 'marketingMoment'}
              />
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default GeneralInfo;
